import React, { useState, useEffect, useCallback, useRef } from 'react';
import AppQuickNav from '../components/AppQuickNav';   // 🧭 швидкий перехід по розділах вкладки
import {
  Box, Typography, Paper, Button, Chip, Stack, Alert,
  CircularProgress, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Avatar, Switch, TextField, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab, Badge, MenuItem,
} from '@mui/material';

// Доступні моделі для рівнів глибини. Підпис пояснює, ЩО обираємо, без технічних деталей.
const CHAT_MODELS = [
  { v: 'gpt-4o-mini', label: 'gpt-4o-mini — швидка й дешева' },
  { v: 'gpt-4o',      label: 'gpt-4o — розумніша, дорожча' },
  { v: 'gpt-4.1',     label: 'gpt-4.1 — найкраща якість тексту' },
  { v: 'gpt-4.1-mini',label: 'gpt-4.1-mini — компроміс ціна/якість' },
];
const AUTH = () => ({ Authorization: `Bearer ${localStorage.getItem('dashboard_token') || ''}` });
const JSON_AUTH = () => ({ ...AUTH(), 'Content-Type': 'application/json' });
const API = (path, opts = {}) =>
  fetch('/api/dashboard/chiromancy' + path, { headers: AUTH(), ...opts });
const APIJ = (path, body, method = 'POST') =>
  fetch('/api/dashboard/chiromancy' + path, { method, headers: JSON_AUTH(), body: JSON.stringify(body) });
const rcpt = (p) => `/api/dashboard/chiromancy/receipt?path=${encodeURIComponent(p)}&token=${localStorage.getItem('dashboard_token') || ''}`;

function StatCard({ label, value, sub, color = 'var(--ac1, #7c4dff)' }) {
  return (
    <Paper sx={{ p: 2, bgcolor: 'rgba(26,26,46,0.8)', border: '1px solid #2a2a4a', minWidth: 130, flex: 1 }}>
      <Typography sx={{ color: '#888', fontSize: 12 }}>{label}</Typography>
      <Typography sx={{ color, fontSize: 28, fontWeight: 'bold' }}>{value ?? '—'}</Typography>
      {sub && <Typography sx={{ color: '#666', fontSize: 11 }}>{sub}</Typography>}
    </Paper>
  );
}

// 💎 2026-08-14: «активний» = заходив за останні 7 днів; «тихий» = жодного
// читання долоні і зареєстрований понад 2 дні тому.
const _chDaysAgo = (d) => {
  if (!d) return null;
  const t = new Date(d);
  if (isNaN(t)) return null;
  return Math.floor((Date.now() - t.getTime()) / 86400000);
};
const _chIsActive7 = (u) => { const d = _chDaysAgo(u.last_seen_at); return d != null && d <= 7; };
const _chIsQuiet = (u) => {
  const reg = _chDaysAgo(u.created_at);
  return (u.readings_count ?? 0) === 0 && reg != null && reg >= 2;
};

// ── 🎵 Фонова музика міні-застосунку ──
// Звук тягнемо ЧЕРЕЗ дашборд: CSP має `media-src 'self' … mediabot.uk` без домену
// Хіромантії, тож пряме посилання браузер тихо блокує. А оскільки <audio src> не
// надсилає токен авторизації — вантажимо авторизованим запитом у blob:.
const MUSIC_AUDIO = (fn) =>
  '/api/dashboard/chiromancy/music/audio?filename=' + encodeURIComponent(fn);

const MUSIC_ERRORS = {
  not_audio: 'Це не аудіофайл або він порожній. Спробуй перезберегти трек локально й завантажити ще раз — так буває з файлами з хмарних тек, які ще не звантажені на диск.',
  bad_audio_type: 'Такий формат не підтримується. Потрібен mp3, ogg, m4a, aac або wav.',
  file_too_large: 'Файл більший за 100 МБ.',
  no_audio_stream: 'У файлі немає звукової доріжки (відео без звуку?).',
  convert_failed: 'Не вдалось витягти звук із файла — спробуй інший файл або формат.',
  audio_too_large: 'Звук після обробки більший за 30 МБ — завеликий для застосунку (задовгий трек?).',
  ffmpeg_missing: 'На сервері Хіромантії немає ffmpeg — напиши розробнику.',
  empty_file: 'Файл порожній.',
  no_file: 'Файл не надіслано.',
};

function PalmMusicSection() {
  const [tracks, setTracks] = useState([]);
  const [load, setLoad] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [names, setNames] = useState({});
  const [audio, setAudio] = useState({});
  const inp = useRef(null);

  useEffect(() => () => Object.values(audio).forEach(u => URL.revokeObjectURL(u)), [audio]);

  const reload = useCallback(() => {
    setLoad(true); setErr('');
    API('/music').then(r => r.json()).then(d => {
      if (Array.isArray(d)) setTracks(d);
      else setErr('Помилка формату відповіді');
    }).catch(() => setErr('Хіромантія недоступна')).finally(() => setLoad(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const upload = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setBusy(true); setMsg('');
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('title', f.name.replace(/\.[^.]+$/, ''));
      const r = await fetch('/api/dashboard/chiromancy/music',
        { method: 'POST', headers: AUTH(), body: fd });
      let d = null;
      try { d = await r.json(); } catch (_p) { /* не-JSON — покажемо статус нижче */ }
      if (d && d.id) { setMsg('✅ Трек додано: ' + (d.title || d.filename)); reload(); }
      else {
        const code = d && d.detail;
        setMsg('❌ ' + (MUSIC_ERRORS[code] || code || `сервер відповів HTTP ${r.status}`));
      }
    } catch (_e) {
      setMsg('❌ Не вдалось звʼязатися з сервером');
    } finally {
      setBusy(false);
      if (inp.current) inp.current.value = '';
    }
  };

  const listen = async (t) => {
    if (audio[t.id]) return;
    try {
      const r = await fetch(MUSIC_AUDIO(t.filename), { headers: AUTH() });
      if (!r.ok) { setMsg(`❌ Не вдалось завантажити трек (HTTP ${r.status})`); return; }
      const url = URL.createObjectURL(await r.blob());
      setAudio(p => ({ ...p, [t.id]: url }));
    } catch (_e) { setMsg('❌ Не вдалось завантажити трек'); }
  };

  const toggle = (t) => API(`/music/${t.id}/toggle`, { method: 'POST' })
    .then(() => reload()).catch(() => setMsg('❌ Не вдалось перемкнути'));

  const rename = (t) => {
    const fd = new FormData();
    fd.append('title', names[t.id] ?? t.title ?? '');
    fetch(`/api/dashboard/chiromancy/music/${t.id}/name`,
      { method: 'POST', headers: AUTH(), body: fd })
      .then(() => { setMsg('✅ Назву збережено'); reload(); })
      .catch(() => setMsg('❌ Не вдалось перейменувати'));
  };

  const del = (t) => {
    if (!window.confirm(`Видалити трек «${t.title || t.filename}» назавжди?`)) return;
    API(`/music/${t.id}`, { method: 'DELETE' })
      .then(() => { setMsg('🗑 Трек видалено'); reload(); })
      .catch(() => setMsg('❌ Не вдалось видалити'));
  };

  return (
    <Paper sx={{ p: 2, bgcolor: 'rgba(26,26,46,0.8)', border: '1px solid #2a2a4a' }}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography sx={{ color: '#b39ddb', fontWeight: 'bold' }}>🎵 Фонова музика</Typography>
        <Button size="small" variant="contained" disabled={busy}
          onClick={() => inp.current && inp.current.click()}>
          {busy ? 'Завантажую…' : '⬆️ Завантажити трек'}
        </Button>
      </Stack>
      <Typography sx={{ color: '#888', fontSize: 12, mb: 1 }}>
        mp3 · m4a · ogg · wav · flac, а також відео mp4 / mov / webm — до 100 МБ. Сервер сам лишає тільки звук:
        AAC і MP3 копіюються без перекодування (якість та сама), інше — у MP3 найвищої якості.
        Увімкнені треки застосунок обирає випадково при старті.
        Поки не завантажено жодного — блок музики в застосунку прихований.
      </Typography>
      <input ref={inp} type="file" accept="audio/*,video/*,.mp3,.ogg,.m4a,.aac,.wav,.flac,.opus,.mp4,.mov,.m4v,.webm,.mkv"
        style={{ display: 'none' }} onChange={upload} />

      {msg && <Alert severity={msg.startsWith('✅') || msg.startsWith('🗑') ? 'success' : 'error'}
        sx={{ mb: 1 }} onClose={() => setMsg('')}>{msg}</Alert>}

      {load ? <CircularProgress size={20} /> : err ? (
        <Alert severity="error">{err}</Alert>
      ) : tracks.length === 0 ? (
        <Typography sx={{ color: '#666' }}>
          Ще жодного треку — у застосунку зараз тиша.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['ID', 'Назва', 'Прослухати', 'Увімкнено', ''].map(h => (
                  <TableCell key={h} sx={{ color: '#888', borderColor: '#2a2a4a' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tracks.map(t => (
                <TableRow key={t.id}>
                  <TableCell sx={{ color: '#ccc', borderColor: '#2a2a4a' }}>{t.id}</TableCell>
                  <TableCell sx={{ borderColor: '#2a2a4a' }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <TextField size="small" defaultValue={t.title || ''}
                        onChange={e => setNames(p => ({ ...p, [t.id]: e.target.value }))}
                        sx={{ input: { color: '#fff' } }} />
                      <Button size="small" variant="outlined" onClick={() => rename(t)}>Зберегти</Button>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ borderColor: '#2a2a4a' }}>
                    {audio[t.id]
                      ? <audio controls autoPlay src={audio[t.id]} style={{ height: 32 }} />
                      : <Button size="small" variant="outlined" onClick={() => listen(t)}>▶️ Прослухати</Button>}
                  </TableCell>
                  <TableCell sx={{ borderColor: '#2a2a4a' }}>
                    <Switch checked={!!t.active} onChange={() => toggle(t)} />
                  </TableCell>
                  <TableCell sx={{ borderColor: '#2a2a4a' }}>
                    <Button size="small" color="error" onClick={() => del(t)}>🗑</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

// 📍 Звідки прийшов юзер (27.10.2026, OWNER: «прийшов новий юзер, а я не розумію, звідки він взявся»).
// Бекенд пише palm_users.source лише з 27.10 — у старших юзерів джерела немає (крім запрошених).
function chSource(u) {
  const s = u.source || (u.invited_by ? 'ref_' + u.invited_by : '');
  if (s.startsWith('ref_')) return '🔗 запросив ' + (u.referred_by_name || s.slice(4));
  if (s === 'gift') return '🎁 подарунок від друга';
  if (s.startsWith('start:')) return '🏷 посилання з міткою «' + s.slice(6) + '»';
  if (s === 'bot') return '🤖 сам натиснув Start у боті';
  if (s === 'app') return '📱 сам відкрив апку';
  return '— не записувалось (до 27.10)';
}

// 👥 Хто кого привів і скільки на цьому заробив (05.10.2026, прохання OWNER).
// Суми — з книги нарахувань, а не з прапорців: «оплачено» означає, що токени справді нараховано.
const ChRefPairs_STATUS = {
  paid: ['оплачено', '#2e5a32'], pending: ['ще не нараховано', '#444'],
  flagged: ['⚠️ підозра — вирішити нижче', '#7a3a2a'], rejected: ['відхилено', '#555'],
};
function ChRefPairs({ summary, pairs, totals, unit, pendingHint }) {
  const [open, setOpen] = useState(false);
  const sm = summary || [], pr = pairs || [];
  const dt = (s) => (s ? String(s).slice(0, 10).split('-').reverse().join('.') : '');
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography sx={{ color: '#ddd', fontWeight: 700, fontSize: 14, mb: .5 }}>👥 Хто кого привів</Typography>
      {pr.length === 0 ? <Typography sx={{ color: '#777', fontSize: 13 }}>Поки ніхто нікого не привів.</Typography> : (<>
        <Typography sx={{ color: '#999', fontSize: 12.5, mb: 1 }}>
          Усього: <b style={{ color: '#ddd' }}>{(totals && totals.pairs) ?? pr.length}</b> запрошених ·
          запрошувачам нараховано <b style={{ color: '#ddd' }}>{(totals && totals.earned) ?? 0} {unit}</b> ·
          новачкам <b style={{ color: '#ddd' }}>{(totals && totals.invited_bonus) ?? 0} {unit}</b>
        </Typography>
        <Box sx={{ border: '1px solid rgba(150,130,255,.18)', borderRadius: 1.5, overflow: 'hidden', mb: 1 }}>
          <Box sx={{ display: 'flex', px: 1.2, py: .6, bgcolor: 'rgba(124,92,255,.12)', fontSize: 12, color: '#aaa', gap: 1 }}>
            <Box sx={{ flex: 1 }}>Хто запрошував</Box><Box sx={{ width: 90, textAlign: 'right' }}>Привів</Box><Box sx={{ width: 110, textAlign: 'right' }}>Заробив</Box>
          </Box>
          {sm.map((s, i) => (
            <Box key={i} sx={{ display: 'flex', px: 1.2, py: .6, borderTop: '1px solid rgba(150,130,255,.1)', fontSize: 13, color: '#ddd', gap: 1 }}>
              <Box sx={{ flex: 1, wordBreak: 'break-word' }}>{s.referrer_name || s.referrer_tid}</Box>
              <Box sx={{ width: 90, textAlign: 'right' }}>{s.invited}</Box>
              <Box sx={{ width: 110, textAlign: 'right', color: s.earned > 0 ? '#39e58c' : '#888', fontWeight: 600 }}>+{s.earned} {unit}</Box>
            </Box>
          ))}
        </Box>
        <Button size="small" onClick={() => setOpen(!open)} sx={{ color: '#9fd', fontSize: 12, px: 0 }}>
          {open ? '▲ Сховати' : '▼ Показати'} усі запрошення ({pr.length})
        </Button>
        {open && pr.map((p, i) => {
          const st = ChRefPairs_STATUS[p.status] || ChRefPairs_STATUS.pending;
          return (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: .6, borderBottom: '1px solid rgba(150,130,255,.1)', flexWrap: 'wrap' }}>
              <Typography sx={{ color: '#888', fontSize: 12, width: 72 }}>{dt(p.created_at)}</Typography>
              <Typography sx={{ color: '#ddd', fontSize: 13, flex: 1, minWidth: 180 }}>{p.referrer_name || p.referrer_tid} → <b>{p.invited_name || p.invited_tid}</b></Typography>
              <Typography sx={{ color: p.inviter_amount > 0 ? '#39e58c' : '#888', fontSize: 12.5 }}>запрошувачу +{p.inviter_amount}</Typography>
              <Typography sx={{ color: p.invited_amount > 0 ? '#39e58c' : '#888', fontSize: 12.5 }}>· новачку +{p.invited_amount}</Typography>
              <Chip size="small" label={st[0]} sx={{ fontSize: 10.5, height: 20, color: '#fff', bgcolor: st[1] }} />
            </Box>);
        })}
        {pendingHint && <Typography sx={{ color: '#777', fontSize: 11.5, mt: .8 }}>{pendingHint}</Typography>}
      </>)}
    </Box>
  );
}

export default function ChiromancyPage() {
  const [tab, setTab] = useState(0);
  const [stats, setStats] = useState(null);
  const [statsErr, setStatsErr] = useState('');

  // Живий годинник для countdown
  const [nowTs, setNowTs] = useState(Date.now());
  const tickRef = useRef(null);
  useEffect(() => {
    tickRef.current = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(tickRef.current);
  }, []);

  const fmtCountdown = (iso) => {
    if (!iso) return '—';
    const ms = new Date(iso).getTime() - nowTs;
    if (ms <= 0) return 'завершився';
    const d = Math.floor(ms / 86400000);
    const h = Math.floor((ms % 86400000) / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return `${d}д ${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  };

  // ── Юзери ────────────────────────────────────────────
  const [users, setUsers] = useState([]);
  // 💎 2026-08-14: фільтр за КОРИСТУВАННЯМ (не за доступом)
  const [uFilter, setUFilter] = useState('all');
  const [uView, setUView] = useState('list');   // 'list' | 'refs' — «хто кого привів» поруч зі списком, як у ZODIAQ
  const [newCount, setNewCount] = useState(0);
  const [usersErr, setUsersErr] = useState('');
  const [usersLoad, setUsersLoad] = useState(true);
  const [userDetail, setUserDetail] = useState(null);
  const [creditInput, setCreditInput] = useState('');
  const [chGiftNotify, setChGiftNotify] = useState(true);  // 🎉 вітання у бот
  const [chGiftBusy, setChGiftBusy] = useState(false);
  const [creditMsg, setCreditMsg] = useState('');

  // Преміум видача (окремий Dialog)
  const [premDlg, setPremDlg] = useState(null);
  const [premMonths, setPremMonths] = useState(1);
  const [premMsg, setPremMsg] = useState('');

  // Ручний ліміт читань (окремий Dialog)
  const [limitsDlg, setLimitsDlg] = useState(null);
  const [limitsVal, setLimitsVal] = useState(0);
  const [limitsMsg, setLimitsMsg] = useState('');

  const loadStats = useCallback(async () => {
    setStatsErr('');
    try {
      const r = await API('/stats');
      if (!r.ok) throw new Error('HTTP ' + r.status);
      setStats(await r.json());
    } catch (e) { setStatsErr(e.message); }
  }, []);

  const loadUsers = useCallback(async () => {
    setUsersLoad(true); setUsersErr('');
    try {
      const r = await API('/users');
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const d = await r.json();
      const list = d.users || [];
      setUsers(list);
      setNewCount(list.filter(u => u.is_new).length);
    } catch (e) { setUsersErr(e.message); }
    setUsersLoad(false);
  }, []);

  useEffect(() => { loadStats(); loadUsers(); }, [loadStats, loadUsers]);

  const markSeen = useCallback(async (tid, all) => {
    try {
      await fetch('/api/dashboard/chiromancy/user-seen',
        { method: 'POST', headers: JSON_AUTH(), body: JSON.stringify(all ? { all: true } : { tid: String(tid) }) });
    } catch (e) { /* тихо */ }
    setUsers(us => us.map(u => (all || u.telegram_id === tid) ? { ...u, is_new: false } : u));
    setNewCount(c => all ? 0 : Math.max(0, c - 1));
  }, []);

  const grantPrem = async () => {
    setPremMsg('');
    try {
      const r = await APIJ('/users/' + premDlg.uid + '/premium', { months: premMonths, action: 'grant' });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      setPremMsg('✅ Преміум видано на ' + premMonths + ' міс.');
      loadUsers(); loadStats();
    } catch (e) { setPremMsg('❌ ' + e.message); }
  };

  const grantCredits = async () => {
    const n = parseInt(creditInput);
    if (!n) return;
    setCreditMsg('…'); setChGiftBusy(true);
    try {
      const r = await APIJ('/credits', { user_id: userDetail.id, credits: n, notify: chGiftNotify });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const d = await r.json();
      // Показуємо ЩО САМЕ сталося з вітанням: «нараховано» без цього рядка
      // виглядає як повний успіх навіть тоді, коли юзер нічого не отримав.
      let m = (n > 0 ? '✅ Подаровано ' + n : '✅ Знято ' + (-n)) + ' · баланс: ' + (d.balance ?? '?');
      if (n > 0 && chGiftNotify) {
        m += d.notified
          ? ' · вітання надіслано' + (d.notify_how === 'photo' ? ' 🖼' : ' (текстом)') + (d.notify_effect ? ' 🎉' : '')
          : ' · ⚠️ вітання НЕ дійшло: ' + (d.notify_error || 'невідома причина');
      } else if (n > 0) m += ' · без вітання';
      setCreditMsg(m);
      setUserDetail(u => ({ ...u, credits: d.balance }));
    } catch (e) { setCreditMsg('❌ ' + e.message); }
    setChGiftBusy(false);
  };

  const revokePrem = async (uid) => {
    if (!window.confirm('Відкликати преміум?')) return;
    try {
      await APIJ('/users/' + uid + '/premium', { action: 'revoke' });
      loadUsers(); loadStats();
      if (userDetail && userDetail.id === uid) setUserDetail(d => ({ ...d, is_premium: false, premium_until: null }));
    } catch (e) { /* тихо */ }
  };

  const setLimits = async () => {
    setLimitsMsg('');
    try {
      const r = await APIJ('/users/' + limitsDlg.uid + '/limits', { extra_readings: limitsVal });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      setLimitsMsg('✅ Ліміт встановлено: +' + limitsVal + ' читань');
      loadUsers();
    } catch (e) { setLimitsMsg('❌ ' + e.message); }
  };

  // ── Платежі ──────────────────────────────────────────
  const [payments, setPayments] = useState([]);
  const [paymentsErr, setPaymentsErr] = useState('');
  const [paymentsLoad, setPaymentsLoad] = useState(false);
  const [payStatus, setPayStatus] = useState('pending');
  const [payMsg, setPayMsg] = useState('');
  const [manualPays, setManualPays] = useState([]);
  const [receiptView, setReceiptView] = useState(null);
  const [manualStatus, setManualStatus] = useState('pending');
  const [manualTotals, setManualTotals] = useState({});
  const [pendingCount, setPendingCount] = useState(0);
  const [editMp, setEditMp] = useState(null);
  const [editCredits, setEditCredits] = useState(0);
  const [editStatus, setEditStatus] = useState('approved');
  const [finance, setFinance] = useState({});
  const [financeOpen, setFinanceOpen] = useState(false);
  const [costsLocal, setCostsLocal] = useState([]);
  const [unitNano, setUnitNano] = useState(0.12);
  const [unitGpt, setUnitGpt] = useState(0.01);
  const [referrals, setReferrals] = useState([]);
  const [refData, setRefData] = useState({ summary: [], totals: null });
  const [refFlags, setRefFlags] = useState([]);

  const loadPayments = useCallback(async (status = payStatus) => {
    setPaymentsLoad(true); setPaymentsErr('');
    try {
      const r = await API('/payments?status=' + status);
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const d = await r.json();
      setPayments(d.payments || []);
    } catch (e) { setPaymentsErr(e.message); }
    setPaymentsLoad(false);
  }, [payStatus]);

  const loadManual = useCallback(async (st) => {
    try { const r = await API('/manual-list?status=' + (st || 'pending')); if (r.ok) { const d = await r.json(); setManualPays(d.items || []); setManualTotals(d); setPendingCount(d.pending_count || 0); } } catch (e) { /* тихо */ }
  }, []);
  const loadFinance = useCallback(async () => {
    try { const r = await API('/finance'); if (r.ok) setFinance(await r.json()); } catch (e) { /* тихо */ }
  }, []);
  const reviewManual = async (id, action) => {
    try { await APIJ('/manual-review', { id, action }); loadManual(manualStatus); loadStats(); } catch (e) { /* тихо */ }
  };
  const editManual = async () => {
    if (!editMp) return;
    try { await APIJ('/manual-edit', { id: editMp.id, credits: parseInt(editCredits) || editMp.credits, status: editStatus }); setEditMp(null); loadManual(manualStatus); loadStats(); } catch (e) { /* тихо */ }
  };
  const saveCost = async (c) => { try { await APIJ('/cost-edit', { id: c.id, label: c.label, amount: parseFloat(c.amount) || 0, currency: c.currency }); loadFinance(); } catch (e) { /* тихо */ } };
  const deleteCost = async (id) => { try { await APIJ('/cost-delete', { id }); setCostsLocal(cs => cs.filter(c => c.id !== id)); loadFinance(); } catch (e) { /* тихо */ } };
  const addCost = async () => { try { await APIJ('/cost-add', { label: 'Нова затрата', amount: 0, currency: 'USD' }); const r = await API('/finance'); if (r.ok) { const d = await r.json(); setFinance(d); setCostsLocal((d.costs || []).map(c => ({ ...c }))); } } catch (e) { /* тихо */ } };
  const saveUnits = async () => { try { await APIJ('/finance-units', { nano_unit_usd: parseFloat(unitNano) || 0.12, gpt_unit_usd: parseFloat(unitGpt) || 0.01 }); loadFinance(); } catch (e) { /* тихо */ } };
  const [refFlagged, setRefFlagged] = useState([]);
  const [refBonusAmt, setRefBonusAmt] = useState(0);
  const [flagMsg, setFlagMsg] = useState('');
  const loadFlagged = useCallback(async () => {
    try {
      const r = await API('/ref-flagged');
      if (r.ok) { const d = await r.json(); setRefFlagged(d.flagged || []); setRefBonusAmt(d.bonus_amount || 0); }
    } catch (e) { /* тихо */ }
  }, []);
  const resolveFlag = async (invited_tid, action) => {
    setFlagMsg('');
    try {
      const r = await APIJ('/ref-resolve', { invited_tid, action });
      const d = await r.json();
      if (!r.ok || d.detail) throw new Error(d.detail || ('HTTP ' + r.status));
      setFlagMsg(action === 'approve' ? ('✅ Бонус виплачено: ' + (d.paid || 0) + ' кред') : '🚫 Відхилено без виплати');
      loadFlagged();
    } catch (e) { setFlagMsg('❌ ' + e.message); }
  };
  const loadReferrals = useCallback(async () => { try { const r = await API('/referrals'); if (r.ok) { const d = await r.json(); setReferrals(d.referrals || []); setRefData({ summary: d.summary || [], totals: d.totals || null }); } } catch (e) { /* тихо */ } }, []);
  const loadRefFlags = useCallback(async () => { try { const r = await API('/ref-flags'); if (r.ok) { const d = await r.json(); setRefFlags(d.flags || []); } } catch (e) { /* тихо */ } }, []);
  const markFlagsSeen = async () => { try { await APIJ('/ref-flags-seen', {}); loadRefFlags(); loadStats(); } catch (e) { /* тихо */ } };
  const uploadBanner = async (file) => { const fd = new FormData(); fd.append('image', file); setCfgMsg('⏳ Завантаження...'); try { const r = await fetch('/api/dashboard/chiromancy/welcome-upload', { method: 'POST', headers: AUTH(), body: fd }); const d = await r.json(); setCfgMsg(d.ok ? '✅ Банер завантажено' : '❌ ' + (d.detail || 'помилка')); } catch (e) { setCfgMsg('❌ ' + e.message); } };
  useEffect(() => { if (tab === 1) { loadPayments(); loadManual(); } }, [tab, loadPayments, loadManual]);
  useEffect(() => { loadManual(); }, [loadManual]);
  useEffect(() => { loadFinance(); }, [loadFinance]);
  useEffect(() => { if (tab === 2) { loadReferrals(); loadRefFlags(); } }, [tab, loadReferrals, loadRefFlags]);
  useEffect(() => { if (tab === 0) loadReferrals(); }, [tab, loadReferrals]);
  useEffect(() => { loadFlagged(); }, [loadFlagged]);

  const approvePayment = async (pid, months) => {
    setPayMsg('');
    try {
      const r = await APIJ('/payments/' + pid + '/approve', { months });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      setPayMsg('✅ Схвалено — преміум видано');
      loadPayments(); loadStats();
    } catch (e) { setPayMsg('❌ ' + e.message); }
  };

  const rejectPayment = async (pid) => {
    const note = window.prompt('Причина відхилення (необов\'язково):') ?? '';
    setPayMsg('');
    try {
      const r = await APIJ('/payments/' + pid + '/reject', { note });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      setPayMsg('✅ Відхилено');
      loadPayments();
    } catch (e) { setPayMsg('❌ ' + e.message); }
  };

  // ── Налаштування ──────────────────────────────────────
  const [cfg, setCfg] = useState(null);
  const [cfgMsg, setCfgMsg] = useState('');
  const [cfgBusy, setCfgBusy] = useState(false);
  const [tariffs, setTariffs] = useState([]);
  const [prices, setPrices] = useState({});
  const [req, setReq] = useState({ monobank: '', privat: '', paypal: '' });
  const [geminiKeyInput, setGeminiKeyInput] = useState('');

  const loadSettings = useCallback(async () => {
    try {
      const r = await API('/settings');
      if (!r.ok) return;
      const d = await r.json();
      const s = d.settings || {};
      setCfg(s);
      setPrices({
        cost_reveal_minor: s.cost_reveal_minor || '20',
        cost_readmore: s.cost_readmore || '20',
        cost_map_item: s.cost_map_item || '2',
        cost_two_hands: s.cost_two_hands || '10',
        cost_second_hand: s.cost_second_hand || '10',
        cost_school_unlock: s.cost_school_unlock || '15',
        cost_school_item: s.cost_school_item || '3',
        min_credits: s.min_credits || '10',
        welcome_credits: s.welcome_credits || '20',
        cost_scan: s.cost_scan || '10',
        // ⚠️ ?? а не ||: '0' — справжнє значення (новачку нічого), а не «порожньо».
        // Запасні значення = ті, що в базі на 05.10.2026, щоб «Зберегти» випадково не підняв бонус.
        ref_bonus_inviter: s.ref_bonus_inviter ?? '5',
        ref_bonus_invited: s.ref_bonus_invited ?? '0',
        chat_tier1_label: s.chat_tier1_label || '',
        chat_tier1_sub: s.chat_tier1_sub || '',
        chat_tier1_model: s.chat_tier1_model || 'gpt-4o-mini',
        chat_tier1_cost: s.chat_tier1_cost || '2',
        chat_tier1_tokens: s.chat_tier1_tokens || '400',
        chat_tier2_label: s.chat_tier2_label || '',
        chat_tier2_sub: s.chat_tier2_sub || '',
        chat_tier2_model: s.chat_tier2_model || 'gpt-4o',
        chat_tier2_cost: s.chat_tier2_cost || '4',
        chat_tier2_tokens: s.chat_tier2_tokens || '700',
        chat_tier3_label: s.chat_tier3_label || '',
        chat_tier3_sub: s.chat_tier3_sub || '',
        chat_tier3_model: s.chat_tier3_model || 'gpt-4o',
        chat_tier3_cost: s.chat_tier3_cost || '8',
        chat_tier3_tokens: s.chat_tier3_tokens || '1100',
        ref_free_limit: s.ref_free_limit || '3',
        ref_per_eur: s.ref_per_eur || '2',
        ref_daily_limit: s.ref_daily_limit || '5',
        ref_lifetime_cap: s.ref_lifetime_cap || '50',
        ref_burst_count: s.ref_burst_count || '8',
        gift_min_credits: s.gift_min_credits || '5',
        gift_expiry_days: s.gift_expiry_days || '7',
        welcome_banner_text: s.welcome_banner_text || '',
        cost_chat: s.cost_chat || '2',
        credits_help_text: s.credits_help_text || '',
        revolut_recipient: s.revolut_recipient || '',
        crypto_address: s.crypto_address || '',
        crypto_label: s.crypto_label || 'USDT (TRC20)',
        usd_per_eur: s.usd_per_eur || '1.08',
      });
      try { setTariffs(JSON.parse(s.tariffs || '[]')); } catch { setTariffs([]); }
      try { setReq(JSON.parse(s.requisites || '{}')); } catch { setReq({}); }
    } catch (e) { /* тихо */ }
  }, []);

  useEffect(() => { if (tab === 2) loadSettings(); }, [tab, loadSettings]);

  const saveSettings = async (patch) => {
    setCfgBusy(true); setCfgMsg('');
    try {
      const r = await APIJ('/settings', patch);
      if (!r.ok) throw new Error('HTTP ' + r.status);
      setCfgMsg('✅ Збережено.');
      loadSettings();
    } catch (e) { setCfgMsg('❌ ' + e.message); }
    setCfgBusy(false);
  };

  const saveTariffs = () => {
    const cleaned = tariffs.map(t => ({
      id: String(t.id || t.months + 'm'), months: parseInt(t.months) || 1,
      price: Number(t.price) || 0, currency: (t.currency || 'USD').slice(0, 8),
      enabled: t.enabled !== false,
    }));
    saveSettings({ tariffs: JSON.stringify(cleaned) });
  };
  const saveRequisites = () => saveSettings({ requisites: JSON.stringify(req) });
  const toggleMono = () => {
    const newVal = cfg?.premium_enabled === 'true' ? 'false' : 'true';
    setCfg(c => ({ ...c, premium_enabled: newVal }));
    saveSettings({ premium_enabled: newVal });
  };
  const toggleNano = () => {
    const newVal = cfg?.nano_banana_enabled === 'true' ? 'false' : 'true';
    setCfg(c => ({ ...c, nano_banana_enabled: newVal }));
    saveSettings({ nano_banana_enabled: newVal });
  };
  const toggleChat = () => {
    const newVal = cfg?.chat_enabled === 'false' ? 'true' : 'false';
    setCfg(c => ({ ...c, chat_enabled: newVal }));
    saveSettings({ chat_enabled: newVal });
  };
  const toggleRevolut = () => {
    const newVal = cfg?.pay_show_revolut === 'true' ? 'false' : 'true';
    setCfg(c => ({ ...c, pay_show_revolut: newVal }));
    saveSettings({ pay_show_revolut: newVal });
  };
  const toggleCard = () => {
    const newVal = cfg?.pay_show_card === 'true' ? 'false' : 'true';
    setCfg(c => ({ ...c, pay_show_card: newVal }));
    saveSettings({ pay_show_card: newVal });
  };
  const toggleCrypto = () => {
    const newVal = cfg?.pay_show_crypto === 'true' ? 'false' : 'true';
    setCfg(c => ({ ...c, pay_show_crypto: newVal }));
    saveSettings({ pay_show_crypto: newVal });
  };
  const saveGeminiKey = () => {
    const v = geminiKeyInput.trim();
    if (!v) return;
    saveSettings({ gemini_key: v });
    setGeminiKeyInput('');
  };
  const addTariff = () => setTariffs(ts => [...ts, { id: 't' + ts.length, months: 1, price: 5, currency: 'USD', enabled: true }]);
  const removeTariff = (i) => setTariffs(ts => ts.filter((_, j) => j !== i));
  const editTariff = (i, key, val) => setTariffs(ts => ts.map((t, j) => j === i ? { ...t, [key]: val } : t));

  // ── Розсилка ─────────────────────────────────────────
  const [bcastText, setBcastText] = useState('');
  const [bcastMsg, setBcastMsg] = useState('');
  const [bcastBusy, setBcastBusy] = useState(false);

  const sendBroadcast = async () => {
    if (!bcastText.trim()) return;
    if (!window.confirm(`Надіслати повідомлення ВСІМ юзерам (${users.length})?`)) return;
    setBcastBusy(true); setBcastMsg('');
    try {
      const r = await APIJ('/broadcast', { text: bcastText });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const d = await r.json();
      setBcastMsg(`✅ Надіслано: ${d.sent} / Помилок: ${d.failed}`);
      setBcastText('');
    } catch (e) { setBcastMsg('❌ ' + e.message); }
    setBcastBusy(false);
  };

  // ── Helpers ───────────────────────────────────────────
  const fmtDate = (s) => s ? new Date(s).toLocaleDateString('uk-UA') : '—';
  const fmtName = (u) => [u.first_name, u.last_name].filter(Boolean).join(' ') || 'Без імені';

  const sx = {
    paper: { bgcolor: 'rgba(26,26,46,0.8)', border: '1px solid #2a2a4a' },
    th: { color: '#888', borderColor: '#2a2a4a', bgcolor: 'rgba(0,0,0,0.3)' },
    td: { color: '#ccc', borderColor: '#2a2a4a' },
    tf: { '& .MuiInputBase-input': { color: '#ccc' }, '& .MuiInputLabel-root': { color: '#888' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' } },
    dlg: { bgcolor: '#140f24', color: '#eee9ff' },
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ color: '#b39ddb', mb: 2, fontWeight: 'bold' }}>
        🖐 Хіромантія
      </Typography>

      {statsErr && <Alert severity="warning" sx={{ mb: 2 }}>{statsErr} — backend недоступний</Alert>}
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
        <StatCard label="Юзерів" value={stats?.total_users} />
        <StatCard label="Нових сьогодні" value={stats?.new_today} color="#4fc3f7" />
        <StatCard label="Преміум" value={stats?.premium} color="#ffd54f" />
        <StatCard label="Читань всього" value={stats?.total_readings} color="#81c784" />
        <StatCard label="Читань сьогодні" value={stats?.readings_today} color="#4db6ac" />
        <Box onClick={() => { setCostsLocal((finance.costs || []).map(c => ({ ...c }))); setUnitNano(finance.nano_unit_usd ?? 0.12); setUnitGpt(finance.gpt_unit_usd ?? 0.01); setFinanceOpen(true); }} sx={{ cursor: 'pointer', flex: 1, minWidth: 130, display: 'flex' }}>
          <StatCard label="💰 Чистий дохід €" value={finance.net_eur != null ? finance.net_eur.toFixed(2) : '—'} color={(finance.net_eur ?? 0) >= 0 ? '#81c784' : '#ef9a9a'} sub="натисни — деталі" />
        </Box>
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)}
        TabIndicatorProps={{ style: { backgroundColor: 'var(--ac1, #7c4dff)' } }}
        sx={{ mb: 2, '& .MuiTab-root': { color: '#888' }, '& .Mui-selected': { color: 'var(--ac1, #7c4dff)' } }}>
        <Tab label={<Badge badgeContent={newCount} color="error" max={99}
                           sx={{ '& .MuiBadge-badge': { right: -10, top: 4 } }}>👥 Юзери</Badge>} />
        <Tab label={<Badge badgeContent={pendingCount} color="error" max={99} sx={{ '& .MuiBadge-badge': { right: -10, top: 4 } }}>💳 Платежі</Badge>} />
        <Tab label="⚙️ Налаштування" />
        <Tab label="📣 Розсилка" />
        <Tab label={<Badge badgeContent={refFlagged.filter(f => !f.bonus_paid).length} color="warning" max={99} sx={{ '& .MuiBadge-badge': { right: -10, top: 4 } }}>⚠️ Підозри</Badge>} />
        <Tab label="🎵 Музика" />
      </Tabs>
      <AppQuickNav />

      {/* ══ ЮЗЕРИ ══ */}
      {tab === 0 && (
        <Paper sx={sx.paper}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ color: '#b39ddb', fontWeight: 'bold' }}>
                Юзери ({users.length})
              </Typography>
              {newCount > 0 && (
                <Chip label={'🔴 ' + newCount + ' нових'} size="small"
                  sx={{ bgcolor: 'rgba(255,107,107,.2)', color: '#ff6b6b' }} />
              )}
            </Box>
            <Stack direction="row" spacing={1}>
              {newCount > 0 && (
                <Button size="small" onClick={() => markSeen(null, true)}
                  sx={{ color: '#ff6b6b', fontSize: 11, border: '1px solid rgba(255,107,107,.3)', px: 1.5 }}>
                  ✓ Позначити всі
                </Button>
              )}
              <Button size="small" onClick={loadUsers} sx={{ color: 'var(--ac1, #7c4dff)' }}>🔄 Оновити</Button>
            </Stack>
          </Box>

          <Stack direction="row" spacing={1} sx={{ px: 2, pb: 1.5, flexWrap: 'wrap' }}>
            {[['list', '👥 Список'], ['refs', '🔗 Хто кого привів · ' + referrals.length]].map(([k, lbl]) => (
              <Chip key={k} label={lbl} onClick={() => setUView(k)} size="small"
                sx={{ cursor: 'pointer', fontWeight: uView === k ? 700 : 400,
                      bgcolor: uView === k ? 'rgba(var(--ac1rgb, 124, 77, 255),0.45)' : 'rgba(255,255,255,0.06)',
                      color: uView === k ? '#fff' : '#aaa' }} />
            ))}
          </Stack>

          {uView === 'refs' ? (
            <Box sx={{ px: 2, pb: 2 }}>
              <ChRefPairs summary={refData.summary} pairs={referrals} totals={refData.totals} unit="ток."
                pendingHint="Бонуси й ліміти — у «⚙️ Налаштування» → «🎁 Реферали». Звідки прийшов кожен юзер — колонка «📍 Звідки» у списку." />
            </Box>
          ) : usersLoad ? (
            <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress sx={{ color: 'var(--ac1, #7c4dff)' }} /></Box>
          ) : usersErr ? (
            <Alert severity="warning" sx={{ m: 2 }}>{usersErr}</Alert>
          ) : (
            <TableContainer>
              {/* 💎 2026-08-14 (OWNER): КОРИСТУВАННЯ, а не доступ */}
              <Stack direction="row" spacing={1} sx={{ mb: 1.5, px: 1, flexWrap: 'wrap' }}>
                {[['all', 'Всі'], ['active7', '🔥 Активні (7д)'], ['quiet', '😴 Тихі']].map(([k, lbl]) => {
                  const _n = k === 'all' ? users.length
                    : k === 'active7' ? users.filter(_chIsActive7).length
                    : users.filter(_chIsQuiet).length;
                  return (
                    <Chip key={k} label={`${lbl} · ${_n}`} onClick={() => setUFilter(k)} size="small"
                      sx={{ cursor: 'pointer', fontWeight: uFilter === k ? 700 : 400,
                            bgcolor: uFilter === k ? 'rgba(var(--ac1rgb, 124, 77, 255),0.45)' : 'rgba(255,255,255,0.06)',
                            color: uFilter === k ? '#fff' : '#aaa' }} />
                  );
                })}
              </Stack>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': sx.th }}>
                    <TableCell>#</TableCell>
                    <TableCell>Ім'я</TableCell>
                    <TableCell>Username</TableCell>
                    <TableCell>📍 Звідки</TableCell>
                    <TableCell>💰 Кредити</TableCell>
                    <TableCell>Днів</TableCell>
                    <TableCell>Читань</TableCell>
                    <TableCell>Преміум</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>⏳ Залишилось</TableCell>
                    <TableCell>Telegram ID</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.filter(u => uFilter === 'active7' ? _chIsActive7(u)
                                    : uFilter === 'quiet' ? _chIsQuiet(u) : true).map((u, i) => (
                    <TableRow key={u.id}
                      onClick={() => { setUserDetail(u); if (u.is_new) markSeen(u.telegram_id); }}
                      sx={{ cursor: 'pointer',
                            bgcolor: u.is_new ? 'rgba(var(--ac1rgb, 124, 77, 255),0.08)' : 'transparent',
                            '& td': sx.td,
                            '&:hover': { bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.12)' } }}>
                      <TableCell sx={{ color: u.is_new ? '#ff9a9a' : '#666' }}>
                        {u.is_new ? '🔴' : (i + 1)}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 26, height: 26, bgcolor: 'var(--ac1, #7c4dff)', fontSize: 11 }}>
                            {(u.first_name || '?')[0]}
                          </Avatar>
                          <Typography sx={{ fontSize: 13, color: u.is_new ? '#ff9a9a' : '#eee', fontWeight: u.is_new ? 700 : 400 }}>
                            {fmtName(u)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: '#22d3ee' }}>
                        {u.username ? '@' + u.username : '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: '#aaa', whiteSpace: 'nowrap' }}>{chSource(u)}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#39e58c' }}>
                        {u.credits ?? 0}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#b39ddb' }}>
                        {u.active_days ?? '—'}
                      </TableCell>
                      <TableCell>{u.readings_count ?? 0}</TableCell>
                      <TableCell>
                        {u.is_premium
                          ? <Chip label="💎 Преміум" size="small" sx={{ bgcolor: 'rgba(255,213,79,0.2)', color: '#ffd54f', fontSize: 11 }} />
                          : <Chip label="Free" size="small" sx={{ bgcolor: 'rgba(128,128,128,0.15)', color: '#888', fontSize: 11 }} />}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, fontFamily: 'monospace', whiteSpace: 'nowrap',
                        color: u.is_premium ? '#ffd479' : '#555' }}>
                        {u.is_premium ? fmtCountdown(u.premium_until) : '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, color: '#666', fontFamily: 'monospace' }}>
                        {u.telegram_id}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* ══ ПЛАТЕЖІ ══ */}
      {tab === 1 && (
        <Paper sx={{ ...sx.paper, mb: 2 }}>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
              <Paper sx={{ p: 1.5, bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.12)', border: '1px solid #4a3a7a', flex: 1, minWidth: 160 }}>
                <Typography sx={{ color: '#aaa', fontSize: 11 }}>💰 Вся сума всіх оплат</Typography>
                <Typography sx={{ color: '#b39ddb', fontSize: 24, fontWeight: 'bold' }}>€{(manualTotals.total_all_eur ?? 0).toFixed(2)}</Typography>
                <Typography sx={{ color: '#666', fontSize: 10 }}>{manualTotals.total_all_credits ?? 0} кред · моно + ручні</Typography>
              </Paper>
              <Paper sx={{ p: 1.5, bgcolor: 'rgba(46,125,50,0.12)', border: '1px solid #2e5a32', flex: 1, minWidth: 160 }}>
                <Typography sx={{ color: '#aaa', fontSize: 11 }}>✅ Схвалені ручні</Typography>
                <Typography sx={{ color: '#81c784', fontSize: 24, fontWeight: 'bold' }}>€{(manualTotals.manual_approved_eur ?? 0).toFixed(2)}</Typography>
                <Typography sx={{ color: '#666', fontSize: 10 }}>{manualTotals.manual_approved_count ?? 0} оплат</Typography>
              </Paper>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography sx={{ color: '#b39ddb', fontWeight: 'bold' }}>🧾 Ручні оплати (чеки)</Typography>
              {[['pending', '⏳ Очікують'], ['approved', '✅ Схвалені'], ['rejected', '❌ Відхилені'], ['all', '📋 Усі']].map(([st, lbl]) => (
                <Button key={st} size="small" variant={manualStatus === st ? 'contained' : 'outlined'} onClick={() => { setManualStatus(st); loadManual(st); }} sx={{ fontSize: 11, ...(manualStatus === st ? { bgcolor: 'var(--ac1, #7c4dff)' } : { color: '#888', borderColor: '#333' }) }}>{lbl}</Button>
              ))}
              <Button size="small" onClick={() => loadManual(manualStatus)} sx={{ color: 'var(--ac1, #7c4dff)', ml: 'auto' }}>🔄</Button>
            </Box>
            {manualPays.length === 0 ? (
              <Typography sx={{ color: '#666', fontSize: 13 }}>Порожньо</Typography>
            ) : manualPays.map(mp => (
              <Box key={mp.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', p: 1, borderBottom: '1px solid #2a2a4a', flexWrap: 'wrap' }}>
                {mp.receipt_path && <img src={rcpt(mp.receipt_path)} alt="чек" onClick={() => setReceiptView(rcpt(mp.receipt_path))} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8, border: '1px solid #333', cursor: 'pointer' }} />}
                <Box sx={{ flex: 1, minWidth: 120 }}>
                  <Typography sx={{ fontSize: 13, color: '#ddd' }}>{mp.credits} кред (€{mp.amount_eur}) · {mp.method === 'revolut' ? 'Revolut' : 'Крипта'} {mp.status === 'approved' ? '✅' : mp.status === 'rejected' ? '❌' : '⏳'}</Typography>
                  <Typography sx={{ fontSize: 11, color: '#888' }}>🆔 {mp.telegram_id} · {(mp.created_at || '').slice(0, 16).replace('T', ' ')}{mp.reviewed_by ? ' · ' + mp.reviewed_by : ''}</Typography>
                </Box>
                {mp.status === 'pending' && <Button size="small" variant="contained" onClick={() => reviewManual(mp.id, 'approve')} sx={{ bgcolor: '#2e7d32', fontSize: 11 }}>✅ Надати</Button>}
                {mp.status === 'pending' && <Button size="small" variant="outlined" onClick={() => reviewManual(mp.id, 'reject')} sx={{ color: '#f44336', borderColor: '#f44336', fontSize: 11 }}>❌</Button>}
                <Button size="small" variant="outlined" onClick={() => { setEditMp(mp); setEditCredits(mp.credits); setEditStatus(mp.status); }} sx={{ color: '#b39ddb', borderColor: '#4a3a7a', fontSize: 11 }}>✏️ Ред.</Button>
              </Box>
            ))}
          </Box>
        </Paper>
      )}
      {tab === 1 && (
        <Paper sx={sx.paper}>
          <Box sx={{ p: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {['pending', 'approved', 'rejected'].map(s => (
              <Button key={s} size="small" variant={payStatus === s ? 'contained' : 'outlined'}
                onClick={() => { setPayStatus(s); loadPayments(s); }}
                sx={{ fontSize: 12, ...(payStatus === s ? { bgcolor: 'var(--ac1, #7c4dff)' } : { color: '#888', borderColor: '#333' }) }}>
                {s === 'pending' ? '⏳ Очікують' : s === 'approved' ? '✅ Схвалені' : '❌ Відхилені'}
              </Button>
            ))}
            <Button size="small" onClick={() => loadPayments()} sx={{ color: 'var(--ac1, #7c4dff)', ml: 'auto' }}>🔄</Button>
          </Box>
          {payMsg && <Alert severity={payMsg.startsWith('✅') ? 'success' : 'error'} sx={{ mx: 2, mb: 1 }} onClose={() => setPayMsg('')}>{payMsg}</Alert>}
          {paymentsLoad ? (
            <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress sx={{ color: 'var(--ac1, #7c4dff)' }} /></Box>
          ) : paymentsErr ? (
            <Alert severity="warning" sx={{ m: 2 }}>{paymentsErr}</Alert>
          ) : payments.length === 0 ? (
            <Typography sx={{ color: '#666', p: 3, textAlign: 'center' }}>Платежів немає</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': sx.th }}>
                    <TableCell>ID</TableCell><TableCell>Юзер</TableCell>
                    <TableCell>Сума</TableCell><TableCell>План</TableCell><TableCell>Дата</TableCell>
                    <TableCell>{payStatus === 'pending' ? 'Дії' : 'Примітка'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map(p => (
                    <TableRow key={p.id} sx={{ '& td': sx.td }}>
                      <TableCell>#{p.id}</TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 13 }}>{[p.first_name, p.last_name].filter(Boolean).join(' ') || '?'}</Typography>
                        <Typography sx={{ fontSize: 11, color: '#888' }}>{p.telegram_id}</Typography>
                      </TableCell>
                      <TableCell><Typography sx={{ color: '#81c784', fontWeight: 'bold' }}>€{Number(p.amount_eur || 0).toFixed(2)}</Typography></TableCell>
                      <TableCell>{p.plan_id || '—'}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{fmtDate(p.created_at)}</TableCell>
                      <TableCell>
                        {payStatus === 'pending' ? (
                          <Stack direction="row" spacing={0.5}>
                            <Button size="small" variant="contained"
                              onClick={() => approvePayment(p.id, parseInt(p.plan_id) || 1)}
                              sx={{ bgcolor: '#388e3c', fontSize: 11, px: 1.5 }}>✅ Схвалити</Button>
                            <Button size="small" variant="outlined"
                              onClick={() => rejectPayment(p.id)}
                              sx={{ color: '#f44336', borderColor: '#f44336', fontSize: 11, px: 1.5 }}>❌</Button>
                          </Stack>
                        ) : <Typography sx={{ fontSize: 12, color: '#888' }}>{p.admin_note || '—'}</Typography>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* ══ НАЛАШТУВАННЯ ══ */}
      {tab === 2 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {cfgMsg && <Alert severity={cfgMsg.startsWith('✅') ? 'success' : 'error'} onClose={() => setCfgMsg('')}>{cfgMsg}</Alert>}
          <Paper sx={{ p: 2, ...sx.paper }}>
            <Typography sx={{ color: '#b39ddb', fontWeight: 'bold', mb: 1.5 }}>💳 Монетизація</Typography>
            <FormControlLabel
              control={<Switch checked={cfg?.premium_enabled === 'true'} onChange={toggleMono} disabled={cfgBusy}
                sx={{ '& .Mui-checked .MuiSwitch-thumb': { bgcolor: 'var(--ac1, #7c4dff)' } }} />}
              label={<Typography sx={{ color: '#ccc', fontSize: 13 }}>
                {cfg?.premium_enabled === 'true' ? '✅ Преміум увімкнено' : '⭕ Преміум вимкнено'}
              </Typography>} />
          </Paper>
          <Paper sx={{ p: 2, ...sx.paper }}>
            <Typography sx={{ color: '#b39ddb', fontWeight: 'bold', mb: 1.5 }}>🖐 Малювання ліній (Nano Banana)</Typography>
            <FormControlLabel
              control={<Switch checked={cfg?.nano_banana_enabled === 'true'} onChange={toggleNano} disabled={cfgBusy}
                sx={{ '& .Mui-checked .MuiSwitch-thumb': { bgcolor: 'var(--ac1, #7c4dff)' } }} />}
              label={<Typography sx={{ color: '#ccc', fontSize: 13 }}>
                {cfg?.nano_banana_enabled === 'true' ? '✅ Платне AI-малювання (~$0.04/фото)' : '⭕ Безкоштовно (координати/SVG)'}
              </Typography>} />
            <Typography sx={{ color: '#888', fontSize: 11, mt: 1 }}>
              ✅ УВІМК = gemini-3-pro-image малює по складках (потребує billing). ⭕ ВИМК = безкоштовно. Миттєвий стоп-кран списань.
            </Typography>
            <Box sx={{ mt: 1.5, p: 1, borderRadius: 1, bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.12)' }}>
              <Typography sx={{ color: '#b39ddb', fontSize: 13, fontWeight: 'bold' }}>
                📊 Намальовано: {parseInt(cfg?.nano_count || '0')} фото
              </Typography>
              <Typography sx={{ color: '#ccc', fontSize: 12 }}>
                ≈ ${(parseInt(cfg?.nano_count || '0') * 0.13).toFixed(2)} витрачено (оцінка · $0.13/фото)
              </Typography>
              <Typography sx={{ color: '#888', fontSize: 10, mt: 0.5 }}>
                Точний баланс — в AI Studio → Billing. Це орієнтовний лічильник.
              </Typography>
            </Box>
          </Paper>
          <Paper sx={{ p: 2, ...sx.paper }}>
            <Typography sx={{ color: '#b39ddb', fontWeight: 'bold', mb: 1.5 }}>🔑 Gemini API ключ</Typography>
            <Typography sx={{ color: cfg?.gemini_key ? '#4caf50' : '#f44336', fontSize: 12, mb: 1 }}>
              {cfg?.gemini_key ? '✅ Ключ встановлено' : '⚠️ Ключ не встановлено'}
            </Typography>
            <TextField fullWidth size="small" type="password" label="Новий ключ (встав щоб замінити)"
              value={geminiKeyInput} onChange={e => setGeminiKeyInput(e.target.value)}
              autoComplete="new-password" sx={{ mb: 1, ...sx.tf }} />
            <Button variant="contained" size="small" onClick={saveGeminiKey} disabled={cfgBusy || !geminiKeyInput.trim()}
              sx={{ bgcolor: 'var(--ac1, #7c4dff)' }}>💾 Зберегти ключ</Button>
            <Typography sx={{ color: '#888', fontSize: 11, mt: 1 }}>
              Ключ зберігається на сервері (palm_settings), ніде не відображається. Введи новий щоб замінити старий.
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, ...sx.paper }}>
            <Typography sx={{ color: '#b39ddb', fontWeight: 'bold', mb: 1.5 }}>💰 Кредити і ціни</Typography>
            <Typography sx={{ color: '#888', fontSize: 12, mb: 1.2 }}>1 € = 10 кредитів (фіксовано). Ціни дій у кредитах:</Typography>
            <Stack spacing={1.2}>
              <TextField size="small" type="number" label="Розблокувати мінорні лінії" value={prices.cost_reveal_minor ?? ''} onChange={e => setPrices(p => ({ ...p, cost_reveal_minor: e.target.value }))} sx={{ ...sx.tf }} />
              <TextField size="small" type="number" label="Читати більше (1 тема)" value={prices.cost_readmore ?? ''} onChange={e => setPrices(p => ({ ...p, cost_readmore: e.target.value }))} sx={{ ...sx.tf }} />
              <TextField size="small" type="number" label="Пункт карти хіромантії" value={prices.cost_map_item ?? ''} onChange={e => setPrices(p => ({ ...p, cost_map_item: e.target.value }))} sx={{ ...sx.tf }} />
              <TextField size="small" type="number" label="Порівняння двох рук" value={prices.cost_two_hands ?? ''} onChange={e => setPrices(p => ({ ...p, cost_two_hands: e.target.value }))} sx={{ ...sx.tf }} />
              <TextField size="small" type="number" label="Відкрити школу (систему)" value={prices.cost_school_unlock ?? ''} onChange={e => setPrices(p => ({ ...p, cost_school_unlock: e.target.value }))} sx={{ ...sx.tf }} />
              <TextField size="small" type="number" label="Лінія в школі (пункт)" value={prices.cost_school_item ?? ''} onChange={e => setPrices(p => ({ ...p, cost_school_item: e.target.value }))} sx={{ ...sx.tf }} />
              <TextField size="small" type="number" label="Мінімум поповнення (кред)" value={prices.min_credits ?? ''} onChange={e => setPrices(p => ({ ...p, min_credits: e.target.value }))} sx={{ ...sx.tf }} />
              <TextField size="small" type="number" label="🎁 Вітальні кредити (перший вхід)" value={prices.welcome_credits ?? ''} onChange={e => setPrices(p => ({ ...p, welcome_credits: e.target.value }))} sx={{ ...sx.tf }} />
              <TextField size="small" type="number" label="🖐 Скан долоні (кред, 0=завжди free; 1-й завжди free)" value={prices.cost_scan ?? ''} onChange={e => setPrices(p => ({ ...p, cost_scan: e.target.value }))} sx={{ ...sx.tf }} />
              <TextField size="small" type="number" label="🤚 Скан ДРУГОЇ руки (кред)" value={prices.cost_second_hand ?? ''} onChange={e => setPrices(p => ({ ...p, cost_second_hand: e.target.value }))} sx={{ ...sx.tf }} />
              <Typography sx={{ color: '#888', fontSize: 11, mt: 0.5 }}>ℹ️ Модель: 1-й скан безкоштовно · наступні скани = «Скан долоні» · друга рука = окремий «Скан другої руки» · їх порівняння = «Порівняння двох рук».</Typography>
              <TextField size="small" multiline minRows={2} label="Опис «на що вистачить»" value={prices.credits_help_text ?? ''} onChange={e => setPrices(p => ({ ...p, credits_help_text: e.target.value }))} sx={{ ...sx.tf }} />
              <Typography sx={{ color: '#b39ddb', fontWeight: 'bold', mt: 1 }}>💳 Картка онлайн (Stripe)</Typography>
              <FormControlLabel sx={{ ml: 0 }} control={<Switch size="small" checked={cfg?.pay_show_card === 'true'} onChange={toggleCard} disabled={cfgBusy} sx={{ '& .Mui-checked .MuiSwitch-thumb': { bgcolor: 'var(--ac1, #7c4dff)' } }} />} label={cfg?.pay_show_card === 'true' ? '✅ Картка увімкнена' : '⭕ Картка вимкнена'} />
              <Typography sx={{ color: '#888', fontSize: 11, mt: 0.5 }}>ℹ️ Спільний Stripe-акаунт MediaSystemGroup (той самий, що в AI Assistant і NUMERIQ). Юзер платить карткою, кредити нараховуються самі. Виплата — на Revolut раз на місяць.</Typography>
              <Typography sx={{ color: '#b39ddb', fontWeight: 'bold', mt: 1 }}>💳 Ручна оплата (Revolut / крипта)</Typography>
              <TextField size="small" label="Revolut отримувач (ім'я / @revtag / телефон)" value={prices.revolut_recipient ?? ''} onChange={e => setPrices(p => ({ ...p, revolut_recipient: e.target.value }))} sx={{ ...sx.tf }} />
              <FormControlLabel sx={{ ml: 0 }} control={<Switch size="small" checked={cfg?.pay_show_revolut === 'true'} onChange={toggleRevolut} disabled={cfgBusy} sx={{ '& .Mui-checked .MuiSwitch-thumb': { bgcolor: 'var(--ac1, #7c4dff)' } }} />} label={cfg?.pay_show_revolut === 'true' ? '✅ Revolut увімкнено' : '⭕ Revolut вимкнено'} />
              <TextField size="small" label="Крипто-адреса (гаманець)" value={prices.crypto_address ?? ''} onChange={e => setPrices(p => ({ ...p, crypto_address: e.target.value }))} sx={{ ...sx.tf }} />
              <TextField size="small" label="Мережа/лейбл (напр. USDT (TRC20))" value={prices.crypto_label ?? ''} onChange={e => setPrices(p => ({ ...p, crypto_label: e.target.value }))} sx={{ ...sx.tf }} />
              <FormControlLabel sx={{ ml: 0 }} control={<Switch size="small" checked={cfg?.pay_show_crypto === 'true'} onChange={toggleCrypto} disabled={cfgBusy} sx={{ '& .Mui-checked .MuiSwitch-thumb': { bgcolor: 'var(--ac1, #7c4dff)' } }} />} label={cfg?.pay_show_crypto === 'true' ? '✅ Крипта увімкнена' : '⭕ Крипта вимкнена'} />
              <TextField size="small" type="number" label="Курс USD за EUR (для крипти)" value={prices.usd_per_eur ?? ''} onChange={e => setPrices(p => ({ ...p, usd_per_eur: e.target.value }))} sx={{ ...sx.tf }} />
            </Stack>
            <Button variant="contained" size="small" onClick={() => saveSettings(prices)} disabled={cfgBusy} sx={{ bgcolor: 'var(--ac1, #7c4dff)', mt: 1.5 }}>💾 Зберегти ціни</Button>
          </Paper>
          <Paper sx={{ p: 2, ...sx.paper }}>
            <Typography sx={{ color: '#b39ddb', fontWeight: 'bold', mb: 0.5 }}>🎁 Реферали</Typography>
            <Typography sx={{ color: '#888', fontSize: 12, mb: 1.5 }}>Скільки дати за приведеного друга, ліміти й захист від накрутки. Нижче — хто кого привів і скільки заробив.</Typography>
            <Stack spacing={1.2}>
              <TextField size="small" type="number" label="Реф-бонус запрошувачу (+ток.)" value={prices.ref_bonus_inviter ?? ''} onChange={e => setPrices(p => ({ ...p, ref_bonus_inviter: e.target.value }))} sx={{ ...sx.tf }} />
              <TextField size="small" type="number" label="Реф-бонус запрошеному (+ток.)" value={prices.ref_bonus_invited ?? ''} onChange={e => setPrices(p => ({ ...p, ref_bonus_invited: e.target.value }))} sx={{ ...sx.tf }} />
              <Typography sx={{ color: '#888', fontSize: 11.5, mt: -0.5 }}>
                Запрошувачу — за ПЕРШИЙ в житті вхід друга (+ сповіщення йому). Запрошеному — понад вітальні, один раз
                (до 05.10.2026 це поле ніде не діяло). 0 = нічого. Той самий IP — нікому автоматично, рішення нижче.
              </Typography>
              <TextField size="small" type="number" label="Ліміт запрошень/день" value={prices.ref_daily_limit ?? ''} onChange={e => setPrices(p => ({ ...p, ref_daily_limit: e.target.value }))} sx={{ ...sx.tf }} />
              <TextField size="small" type="number" label="Запрошень без поповнення" value={prices.ref_free_limit ?? ''} onChange={e => setPrices(p => ({ ...p, ref_free_limit: e.target.value }))} sx={{ ...sx.tf }} />
              <TextField size="small" type="number" label="+запрошень за кожен €1" value={prices.ref_per_eur ?? ''} onChange={e => setPrices(p => ({ ...p, ref_per_eur: e.target.value }))} sx={{ ...sx.tf }} />
              <TextField size="small" type="number" label="Стеля запрошень (lifetime, 0=∞)" value={prices.ref_lifetime_cap ?? ''} onChange={e => setPrices(p => ({ ...p, ref_lifetime_cap: e.target.value }))} sx={{ ...sx.tf }} />
              <TextField size="small" type="number" label="Флаг сплеску: ≥N запрошень/10хв" value={prices.ref_burst_count ?? ''} onChange={e => setPrices(p => ({ ...p, ref_burst_count: e.target.value }))} sx={{ ...sx.tf }} />
            </Stack>
            <Button variant="contained" size="small" onClick={() => saveSettings(prices)} disabled={cfgBusy} sx={{ bgcolor: 'var(--ac1, #7c4dff)', mt: 1.5 }}>💾 Зберегти</Button>
            {refFlags.filter(f => !f.seen).length > 0 && <>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <Typography sx={{ color: '#ef9a9a', fontWeight: 'bold' }}>⚠️ Підозри накрутки ({refFlags.filter(f => !f.seen).length})</Typography>
                <Button size="small" onClick={markFlagsSeen} sx={{ color: 'var(--ac1, #7c4dff)', ml: 'auto' }}>Прочитано</Button>
              </Box>
              {refFlags.filter(f => !f.seen).map(f => (
                <Box key={f.id} sx={{ fontSize: 12, color: '#ddd', py: 0.5, borderBottom: '1px solid #2a2a4a' }}>{f.referrer_name || f.referrer_tid} — {f.invites_count} запрошень · {(f.created_at || '').slice(0, 16).replace('T', ' ')}</Box>
              ))}
            </>}
            {/* 05.10.2026: суми з книги замість прапорця «✅ бонус» — той стояв і на ВІДХИЛЕНИХ */}
            <Button size="small" onClick={loadReferrals} sx={{ color: 'var(--ac1, #7c4dff)', mt: 1.5 }}>🔄 Оновити</Button>
            <ChRefPairs summary={refData.summary} pairs={referrals} totals={refData.totals} unit="ток."
              pendingHint="«Ще не нараховано» — друг зайшов, але бонус чекає ліміту (на день / без поповнення) або ще не сканував." />
          </Paper>
          <Paper sx={{ p: 2, ...sx.paper }}>
            <Typography sx={{ color: '#b39ddb', fontWeight: 'bold', mb: 0.5 }}>💬 AI-чат з хіромантом</Typography>
            <Typography sx={{ color: '#888', fontSize: 12, mb: 1.5 }}>Питання хіроманту в застосунку: вмикач, базова ціна і три рівні відповіді (назва кнопки, модель, ціна, довжина).</Typography>
            <Stack spacing={1.2}>
              <FormControlLabel sx={{ ml: 0 }} control={<Switch size="small" checked={cfg?.chat_enabled !== 'false'} onChange={toggleChat} disabled={cfgBusy} sx={{ '& .Mui-checked .MuiSwitch-thumb': { bgcolor: 'var(--ac1, #7c4dff)' } }} />} label={cfg?.chat_enabled !== 'false' ? '✅ Чат хіроманта увімкнено' : '⭕ Чат вимкнено'} />
              <TextField size="small" type="number" label="💬 Чат-питання хіроманту (кред)" value={prices.cost_chat ?? ''} onChange={e => setPrices(p => ({ ...p, cost_chat: e.target.value }))} sx={{ ...sx.tf }} />
              <Box sx={{ width: '100%', mt: 1 }}><Typography sx={{ color: '#b39ddb', fontSize: 12, fontWeight: 700 }}>⚡ Рівень 1 — Швидка відповідь</Typography></Box>
              <TextField size="small" label="Назва кнопки (рівень 1)" value={prices.chat_tier1_label ?? ''} onChange={e => setPrices(p => ({ ...p, chat_tier1_label: e.target.value }))} sx={{ ...sx.tf }} />
              <TextField size="small" label="Підпис під назвою (рівень 1)" value={prices.chat_tier1_sub ?? ''} onChange={e => setPrices(p => ({ ...p, chat_tier1_sub: e.target.value }))} sx={{ ...sx.tf }} />
              <TextField select size="small" label="Модель (рівень 1)" value={prices.chat_tier1_model || 'gpt-4o-mini'} onChange={e => setPrices(p => ({ ...p, chat_tier1_model: e.target.value }))} sx={{ ...sx.tf, minWidth: 230 }}>
                {CHAT_MODELS.map(m => (
                  <MenuItem key={m.v} value={m.v} sx={{ fontSize: 13 }}>{m.label}</MenuItem>
                ))}
              </TextField>
              <TextField size="small" type="number" label="Ціна, кред (рівень 1)" value={prices.chat_tier1_cost ?? ''} onChange={e => setPrices(p => ({ ...p, chat_tier1_cost: e.target.value }))} sx={{ ...sx.tf }} />
              <TextField size="small" type="number" label="Довжина відповіді, токенів (рівень 1)" value={prices.chat_tier1_tokens ?? ''} onChange={e => setPrices(p => ({ ...p, chat_tier1_tokens: e.target.value }))} sx={{ ...sx.tf }} />
              <Box sx={{ width: '100%', mt: 1 }}><Typography sx={{ color: '#b39ddb', fontSize: 12, fontWeight: 700 }}>🔎 Рівень 2 — Детальна відповідь</Typography></Box>
              <TextField size="small" label="Назва кнопки (рівень 2)" value={prices.chat_tier2_label ?? ''} onChange={e => setPrices(p => ({ ...p, chat_tier2_label: e.target.value }))} sx={{ ...sx.tf }} />
              <TextField size="small" label="Підпис під назвою (рівень 2)" value={prices.chat_tier2_sub ?? ''} onChange={e => setPrices(p => ({ ...p, chat_tier2_sub: e.target.value }))} sx={{ ...sx.tf }} />
              <TextField select size="small" label="Модель (рівень 2)" value={prices.chat_tier2_model || 'gpt-4o-mini'} onChange={e => setPrices(p => ({ ...p, chat_tier2_model: e.target.value }))} sx={{ ...sx.tf, minWidth: 230 }}>
                {CHAT_MODELS.map(m => (
                  <MenuItem key={m.v} value={m.v} sx={{ fontSize: 13 }}>{m.label}</MenuItem>
                ))}
              </TextField>
              <TextField size="small" type="number" label="Ціна, кред (рівень 2)" value={prices.chat_tier2_cost ?? ''} onChange={e => setPrices(p => ({ ...p, chat_tier2_cost: e.target.value }))} sx={{ ...sx.tf }} />
              <TextField size="small" type="number" label="Довжина відповіді, токенів (рівень 2)" value={prices.chat_tier2_tokens ?? ''} onChange={e => setPrices(p => ({ ...p, chat_tier2_tokens: e.target.value }))} sx={{ ...sx.tf }} />
              <Box sx={{ width: '100%', mt: 1 }}><Typography sx={{ color: '#b39ddb', fontSize: 12, fontWeight: 700 }}>🧠 Рівень 3 — Глибока відповідь</Typography></Box>
              <TextField size="small" label="Назва кнопки (рівень 3)" value={prices.chat_tier3_label ?? ''} onChange={e => setPrices(p => ({ ...p, chat_tier3_label: e.target.value }))} sx={{ ...sx.tf }} />
              <TextField size="small" label="Підпис під назвою (рівень 3)" value={prices.chat_tier3_sub ?? ''} onChange={e => setPrices(p => ({ ...p, chat_tier3_sub: e.target.value }))} sx={{ ...sx.tf }} />
              <TextField select size="small" label="Модель (рівень 3)" value={prices.chat_tier3_model || 'gpt-4o-mini'} onChange={e => setPrices(p => ({ ...p, chat_tier3_model: e.target.value }))} sx={{ ...sx.tf, minWidth: 230 }}>
                {CHAT_MODELS.map(m => (
                  <MenuItem key={m.v} value={m.v} sx={{ fontSize: 13 }}>{m.label}</MenuItem>
                ))}
              </TextField>
              <TextField size="small" type="number" label="Ціна, кред (рівень 3)" value={prices.chat_tier3_cost ?? ''} onChange={e => setPrices(p => ({ ...p, chat_tier3_cost: e.target.value }))} sx={{ ...sx.tf }} />
              <TextField size="small" type="number" label="Довжина відповіді, токенів (рівень 3)" value={prices.chat_tier3_tokens ?? ''} onChange={e => setPrices(p => ({ ...p, chat_tier3_tokens: e.target.value }))} sx={{ ...sx.tf }} />
            </Stack>
            <Button variant="contained" size="small" onClick={() => saveSettings(prices)} disabled={cfgBusy} sx={{ bgcolor: 'var(--ac1, #7c4dff)', mt: 1.5 }}>💾 Зберегти</Button>
          </Paper>
          <Paper sx={{ p: 2, ...sx.paper }}>
            <Typography sx={{ color: '#b39ddb', fontWeight: 'bold', mb: 0.5 }}>🎀 Подарунки</Typography>
            <Typography sx={{ color: '#888', fontSize: 12, mb: 1.5 }}>Подарунок кредитів другові: мінімальна сума й скільки днів він чекає, поки друг забере.</Typography>
            <Stack spacing={1.2}>
              <TextField size="small" type="number" label="Дарунок: мін. кредитів" value={prices.gift_min_credits ?? ''} onChange={e => setPrices(p => ({ ...p, gift_min_credits: e.target.value }))} sx={{ ...sx.tf }} />
              <TextField size="small" type="number" label="Дарунок: термін (днів)" value={prices.gift_expiry_days ?? ''} onChange={e => setPrices(p => ({ ...p, gift_expiry_days: e.target.value }))} sx={{ ...sx.tf }} />
            </Stack>
            <Button variant="contained" size="small" onClick={() => saveSettings(prices)} disabled={cfgBusy} sx={{ bgcolor: 'var(--ac1, #7c4dff)', mt: 1.5 }}>💾 Зберегти</Button>
          </Paper>
          <Paper sx={{ p: 2, ...sx.paper }}>
            <Typography sx={{ color: '#b39ddb', fontWeight: 'bold', mb: 0.5 }}>👋 Вітання новачку</Typography>
            <Typography sx={{ color: '#888', fontSize: 12, mb: 1.5 }}>Банер, який бачить людина при першому вході: текст і картинка.</Typography>
            <Stack spacing={1.2}>
              <TextField size="small" multiline minRows={2} label="Текст вітального банера" value={prices.welcome_banner_text ?? ''} onChange={e => setPrices(p => ({ ...p, welcome_banner_text: e.target.value }))} sx={{ ...sx.tf }} />
            </Stack>
            <Button variant="contained" size="small" onClick={() => saveSettings(prices)} disabled={cfgBusy} sx={{ bgcolor: 'var(--ac1, #7c4dff)', mt: 1.5 }}>💾 Зберегти</Button>
            <Box sx={{ mt: 1.5 }}>
              <Typography sx={{ color: '#888', fontSize: 12, mb: 0.5 }}>🖼 Картинка вітального банера:</Typography>
              <Button variant="outlined" size="small" component="label" sx={{ color: '#b39ddb', borderColor: '#4a3a7a' }}>Завантажити картинку
                <input type="file" accept="image/*" hidden onChange={e => e.target.files[0] && uploadBanner(e.target.files[0])} />
              </Button>
            </Box>
          </Paper>
          <Paper sx={{ p: 2, ...sx.paper }}>
            <Typography sx={{ color: '#b39ddb', fontWeight: 'bold', mb: 1.5 }}>📋 Тарифи</Typography>
            {tariffs.map((t, i) => (
              <Stack key={i} direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
                <TextField size="small" label="Місяців" type="number" value={t.months}
                  onChange={e => editTariff(i, 'months', e.target.value)} sx={{ width: 90, ...sx.tf }} />
                <TextField size="small" label="Ціна" type="number" value={t.price}
                  onChange={e => editTariff(i, 'price', e.target.value)} sx={{ width: 90, ...sx.tf }} />
                <TextField size="small" label="Валюта" value={t.currency}
                  onChange={e => editTariff(i, 'currency', e.target.value)} sx={{ width: 80, ...sx.tf }} />
                <Switch size="small" checked={t.enabled !== false} onChange={e => editTariff(i, 'enabled', e.target.checked)} />
                <Button size="small" onClick={() => removeTariff(i)} sx={{ color: '#f44336', minWidth: 0 }}>✕</Button>
              </Stack>
            ))}
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button size="small" onClick={addTariff} sx={{ color: 'var(--ac1, #7c4dff)' }}>+ Додати</Button>
              <Button size="small" variant="contained" onClick={saveTariffs} disabled={cfgBusy} sx={{ bgcolor: 'var(--ac1, #7c4dff)' }}>💾 Зберегти тарифи</Button>
            </Stack>
          </Paper>
          <Paper sx={{ p: 2, ...sx.paper }}>
            <Typography sx={{ color: '#b39ddb', fontWeight: 'bold', mb: 1.5 }}>💰 Реквізити</Typography>
            {['monobank', 'privat', 'paypal'].map(key => (
              <TextField key={key} fullWidth size="small" label={key.charAt(0).toUpperCase() + key.slice(1)}
                value={req[key] || ''} onChange={e => setReq(r => ({ ...r, [key]: e.target.value }))}
                sx={{ mb: 1.5, ...sx.tf }} />
            ))}
            <Button variant="contained" size="small" onClick={saveRequisites} disabled={cfgBusy} sx={{ bgcolor: 'var(--ac1, #7c4dff)' }}>💾 Зберегти</Button>
          </Paper>
        </Box>
      )}

      {/* == flagged referrals == */}
      {/* 2026-10-03 (OWNER): музика — окремою вкладкою сторінки Хіромантії, а не всередині «Налаштувань» */}
      {tab === 5 && <PalmMusicSection />}

      {tab === 4 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Paper sx={{ p: 2, ...sx.paper }}>
            <Typography sx={{ color: '#ffb74d', fontWeight: 'bold', mb: 0.5 }}>⚠️ Підозрілі запрошення</Typography>
            <Typography sx={{ color: '#888', fontSize: 13, mb: 2 }}>
              Той, хто запросив, і той, кого запросили, зайшли з <b>однакового IP</b> — схоже на другий акаунт тієї самої людини.
              Бонус ({refBonusAmt} кред) <b>не виплачено</b>. «Виплатити» — якщо це справжні різні люди (спільний Wi-Fi, родина);
              «Відхилити» — якщо накрутка.
            </Typography>
            {flagMsg && <Alert severity={flagMsg.startsWith('✅') ? 'success' : (flagMsg.startsWith('❌') ? 'error' : 'warning')} sx={{ mb: 2 }}>{flagMsg}</Alert>}
            {refFlagged.length === 0 ? (
              <Typography sx={{ color: '#666', fontSize: 13 }}>Підозрілих запрошень немає 👍</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: '#888' }}>Хто запросив</TableCell>
                      <TableCell sx={{ color: '#888' }}>Кого запросили</TableCell>
                      <TableCell sx={{ color: '#888' }}>Звідки</TableCell>
                      <TableCell sx={{ color: '#888' }}>IP</TableCell>
                      <TableCell sx={{ color: '#888' }}>Скан</TableCell>
                      <TableCell sx={{ color: '#888' }}>Дія</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {refFlagged.map((f, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ color: '#ddd' }}>{f.referrer_name || '—'}<br /><span style={{ color: '#666', fontSize: 11 }}>{f.referrer_tid}</span></TableCell>
                        <TableCell sx={{ color: '#ddd' }}>{f.invited_name || '—'}<br /><span style={{ color: '#666', fontSize: 11 }}>{f.invited_tid}</span></TableCell>
                        <TableCell>
                          <Chip size="small" label={f.source === 'gift' ? ('🎁 подарунок ' + (f.gift_amount || 0)) : '🔗 посилання'}
                            sx={{ bgcolor: f.source === 'gift' ? 'rgba(245,158,11,.15)' : 'rgba(var(--ac1rgb, 124, 77, 255),.15)', color: f.source === 'gift' ? '#ffb74d' : '#b39ddb' }} />
                        </TableCell>
                        <TableCell sx={{ color: '#888', fontSize: 12 }}>{f.ip || '—'}</TableCell>
                        <TableCell sx={{ color: f.scanned ? '#81c784' : '#666', fontSize: 12 }}>{f.scanned ? 'так' : 'ні'}</TableCell>
                        <TableCell>
                          {f.bonus_paid ? <Chip size="small" label="вирішено" sx={{ bgcolor: 'rgba(129,199,132,.15)', color: '#81c784' }} /> : (
                            <Stack direction="row" spacing={1}>
                              <Button size="small" variant="contained" onClick={() => resolveFlag(f.invited_tid, 'approve')}
                                sx={{ bgcolor: '#39e58c', color: '#000', fontSize: 11 }}>Виплатити</Button>
                              <Button size="small" variant="outlined" onClick={() => resolveFlag(f.invited_tid, 'reject')}
                                sx={{ borderColor: '#ef5350', color: '#ef9a9a', fontSize: 11 }}>Відхилити</Button>
                            </Stack>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Box>
      )}

      {/* ══ РОЗСИЛКА ══ */}
      {tab === 3 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Paper sx={{ p: 2, ...sx.paper }}>
            <Typography sx={{ color: '#b39ddb', fontWeight: 'bold', mb: 0.5 }}>📣 Розсилка через бота</Typography>
            <Typography sx={{ color: '#666', fontSize: 13, mb: 2 }}>
              Надіслати повідомлення всім {users.length} юзерам через @Chiromancy_New_bot. Підтримує HTML: <code>&lt;b&gt;</code>, <code>&lt;i&gt;</code>, <code>&lt;a href="..."&gt;</code>.
            </Typography>
            <TextField
              fullWidth multiline rows={5}
              label="Текст повідомлення"
              value={bcastText}
              onChange={e => setBcastText(e.target.value)}
              placeholder="🖐 Привіт! У нас є новини..."
              sx={{ mb: 2, ...sx.tf }}
            />
            {bcastMsg && <Alert severity={bcastMsg.startsWith('✅') ? 'success' : 'error'} sx={{ mb: 2 }} onClose={() => setBcastMsg('')}>{bcastMsg}</Alert>}
            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="contained" onClick={sendBroadcast} disabled={bcastBusy || !bcastText.trim()}
                sx={{ bgcolor: 'var(--ac1, #7c4dff)', '&:hover': { bgcolor: '#6a3de8' } }}>
                {bcastBusy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : '📤 Надіслати всім'}
              </Button>
              <Typography sx={{ color: '#666', fontSize: 12 }}>{users.length} отримувачів</Typography>
            </Stack>
          </Paper>

          <Paper sx={{ p: 2, ...sx.paper }}>
            <Typography sx={{ color: '#b39ddb', fontWeight: 'bold', mb: 1 }}>🔗 Реферальні посилання</Typography>
            <Typography sx={{ color: '#666', fontSize: 13, mb: 1.5 }}>
              Коли новий юзер відкриває бота через реф-посилання — система записує хто його запросив.
            </Typography>
            {users.slice(0, 5).map(u => (
              <Box key={u.id} sx={{ mb: 1, p: 1, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.2)' }}>
                <Typography sx={{ color: '#ccc', fontSize: 12 }}>{fmtName(u)}</Typography>
                <Typography sx={{ color: '#22d3ee', fontSize: 11, fontFamily: 'monospace', userSelect: 'all' }}>
                  {'https://t.me/Chiromancy_New_bot?start=ref_' + u.telegram_id}
                </Typography>
              </Box>
            ))}
            {users.length > 5 && <Typography sx={{ color: '#666', fontSize: 12 }}>… та ще {users.length - 5} юзерів</Typography>}
          </Paper>
        </Box>
      )}

      {/* ══ DIALOG: Деталь юзера ══ */}
      {userDetail && (
        <Dialog open={!!userDetail} onClose={() => setUserDetail(null)} maxWidth="xs" fullWidth
          PaperProps={{ sx: sx.dlg }}>
          <DialogTitle sx={{ bgcolor: '#140f24', color: '#eee9ff', pb: 1 }}>
            {fmtName(userDetail)}
            {userDetail.username && (
              <Typography component="span" sx={{ color: '#22d3ee', ml: 1, fontSize: 14 }}>
                @{userDetail.username}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent sx={{ bgcolor: '#140f24', pt: 1 }}>
            {/* Інфо-рядки */}
            <Stack spacing={0.8} sx={{ mb: 2 }}>
              {[
                ['🆔 Telegram ID', userDetail.telegram_id],
                ['🌐 Мова', userDetail.lang || '—'],
                ['📅 Реєстрація', fmtDate(userDetail.created_at)],
                ['📆 Активних днів', userDetail.active_days ?? '—'],
                ['📖 Читань', userDetail.readings_count ?? 0],
                ['➕ Бонус читань', userDetail.extra_readings > 0 ? '+' + userDetail.extra_readings : '—'],
                ['🕐 Останній візит', fmtDate(userDetail.last_seen_at)],
                ['📍 Звідки прийшов', chSource(userDetail)],
                ['👥 Запросив', String(userDetail.invited_count ?? 0)],
                ['💰 Кредити', String(userDetail.credits ?? 0)],
              ].map(([label, val]) => (
                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.05)', pb: 0.5 }}>
                  <Typography sx={{ color: '#888', fontSize: 12 }}>{label}</Typography>
                  <Typography sx={{ color: '#ccc', fontSize: 12, fontWeight: 500 }}>{String(val)}</Typography>
                </Box>
              ))}
            </Stack>

            {/* 👤 27.10.2026: профіль у Telegram — у новачків часто немає @username, і відкрити їх не було як */}
            <Stack direction="row" sx={{ mb: .6, flexWrap: 'wrap', gap: 1 }}>
              <Button size="small" variant="outlined" href={'tg://user?id=' + userDetail.telegram_id}
                sx={{ color: '#22d3ee', borderColor: 'rgba(34,211,238,.4)' }}>👤 Профіль у Telegram</Button>
              {userDetail.username && (
                <Button size="small" variant="outlined" href={'https://t.me/' + userDetail.username} target="_blank" rel="noopener noreferrer"
                  sx={{ color: '#22d3ee', borderColor: 'rgba(34,211,238,.4)' }}>@{userDetail.username}</Button>
              )}
            </Stack>
            <Typography sx={{ color: '#777', fontSize: 11, mb: 2 }}>
              Без @username кнопка відкриває профіль лише в Telegram, який уже бачив цього юзера. Найпростіше - натиснути його ім'я у сповіщенні бота «Новий юзер».
            </Typography>

            {/* 💰 Кредити — нарахування (OWNER) */}
            <Box sx={{ p: 1.5, borderRadius: 1, mb: 2, bgcolor: 'rgba(124,92,255,.06)', border: '1px solid rgba(124,92,255,.2)' }}>
              <Typography sx={{ color: '#b39ddb', fontWeight: 700, fontSize: 14, mb: 0.8 }}>
                💰 Кредити: {userDetail.credits ?? 0}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                {[5, 10, 25, 50].map(n => (
                  <Chip key={n} label={'+' + n} size="small" clickable onClick={() => setCreditInput(String(n))}
                    sx={{ bgcolor: String(n) === String(creditInput) ? 'rgba(124,92,255,.4)' : 'rgba(255,255,255,.06)',
                      color: String(n) === String(creditInput) ? '#fff' : '#aaa',
                      fontWeight: String(n) === String(creditInput) ? 700 : 400 }} />
                ))}
                <TextField size="small" type="number" placeholder="К-сть" value={creditInput}
                  onChange={e => setCreditInput(e.target.value)} sx={{ width: 92, ...sx.tf }} />
                <Button size="small" variant="contained" onClick={grantCredits} disabled={!creditInput || chGiftBusy}
                  sx={{ bgcolor: 'var(--ac1, #7c4dff)' }}>{chGiftBusy ? '…' : '🎁 Подарувати'}</Button>
              </Stack>
              <FormControlLabel sx={{ mt: .3, ml: 0 }}
                control={<Switch size="small" checked={chGiftNotify} onChange={e => setChGiftNotify(e.target.checked)} />}
                label={<Typography sx={{ color: '#8a83b0', fontSize: 12 }}>🎉 святкове вітання у бот (банер + ефект)</Typography>} />
              {creditMsg && <Typography sx={{ color: creditMsg.startsWith('✅') ? '#39e58c' : '#f44336', fontSize: 11, mt: 0.6 }}>{creditMsg}</Typography>}
            </Box>

            {/* Преміум / Доступ блок — ЗАВЖДИ видимий */}
            <Box sx={{
              p: 1.5, borderRadius: 1, mb: 2,
              bgcolor: userDetail.is_premium ? 'rgba(255,212,121,.10)' : 'rgba(124,92,255,.06)',
              border: userDetail.is_premium ? '1px solid rgba(255,212,121,.3)' : '1px solid rgba(124,92,255,.2)',
            }}>
              {userDetail.is_premium ? (
                <>
                  <Typography sx={{ color: '#ffd54f', fontWeight: 700, fontSize: 14, mb: 0.5 }}>
                    💎 Преміум активний
                  </Typography>
                  <Typography sx={{ color: '#aaa', fontSize: 11, mb: 0.5 }}>
                    діє до: {fmtDate(userDetail.premium_until)}
                  </Typography>
                  <Typography sx={{
                    color: '#39e58c', fontSize: 22, fontWeight: 800, fontFamily: 'monospace',
                    letterSpacing: 1, mt: 0.5
                  }}>
                    ⏳ {fmtCountdown(userDetail.premium_until)}
                  </Typography>
                  <Button size="small" onClick={() => revokePrem(userDetail.id)}
                    sx={{ color: '#f44336', fontSize: 11, mt: 1, p: 0, minWidth: 0 }}>
                    ✕ Відкликати преміум
                  </Button>
                </>
              ) : (() => {
                // free readings: скидаються щодня о 00:00 UTC
                const todayUTC = new Date().toISOString().slice(0, 10);
                const usedDate = (userDetail.free_readings_date || '').slice(0, 10);
                const usedToday = usedDate === todayUTC;
                const freeUsed = usedToday ? (userDetail.free_readings_used ?? 0) : 0;
                const nextMidnight = new Date();
                nextMidnight.setUTCDate(nextMidnight.getUTCDate() + 1);
                nextMidnight.setUTCHours(0, 0, 0, 0);
                const msLeft = nextMidnight.getTime() - nowTs;
                const hLeft = String(Math.floor((msLeft % 86400000) / 3600000)).padStart(2,'0');
                const mLeft = String(Math.floor((msLeft % 3600000) / 60000)).padStart(2,'0');
                const sLeft = String(Math.floor((msLeft % 60000) / 1000)).padStart(2,'0');
                const resetCountdown = `${hLeft}:${mLeft}:${sLeft}`;
                return (
                  <>
                    <Typography sx={{ color: '#b39ddb', fontWeight: 600, fontSize: 13, mb: 0.8 }}>
                      📖 Безкоштовний доступ
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                      <Typography sx={{ color: '#888', fontSize: 12 }}>Використано сьогодні:</Typography>
                      <Typography sx={{ color: freeUsed > 0 ? '#ff9a9a' : '#39e58c', fontSize: 12, fontWeight: 700 }}>
                        {freeUsed} читань
                      </Typography>
                    </Box>
                    {userDetail.extra_readings > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                        <Typography sx={{ color: '#888', fontSize: 12 }}>Бонусних читань:</Typography>
                        <Typography sx={{ color: '#39e58c', fontSize: 12, fontWeight: 700 }}>
                          +{userDetail.extra_readings}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ mt: 0.8, pt: 0.8, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                      {freeUsed > 0 ? (
                        <>
                          <Typography sx={{ color: '#888', fontSize: 11, mb: 0.3 }}>
                            ⏳ Доступ скинеться через (UTC 00:00):
                          </Typography>
                          <Typography sx={{
                            color: '#ffd479', fontSize: 20, fontWeight: 800,
                            fontFamily: 'monospace', letterSpacing: 1
                          }}>
                            {resetCountdown}
                          </Typography>
                        </>
                      ) : (
                        <Typography sx={{ color: '#39e58c', fontSize: 13, fontWeight: 600 }}>
                          ✅ Читання доступні зараз
                        </Typography>
                      )}
                    </Box>
                  </>
                );
              })()}
            </Box>

            {/* Видати преміум */}
            <Typography sx={{ color: '#b39ddb', fontSize: 12, mb: 0.8, fontWeight: 600 }}>⭐ Видати преміум</Typography>
            <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              {[1, 3, 6, 12].map(m => (
                <Chip key={m} label={m + ' міс'} size="small" clickable onClick={() => setPremMonths(m)}
                  sx={{ bgcolor: premMonths === m ? 'rgba(255,213,79,.25)' : 'rgba(124,92,255,.15)',
                    color: premMonths === m ? '#ffd54f' : '#cbb8ff', fontWeight: premMonths === m ? 700 : 400 }} />
              ))}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <TextField size="small" type="number" label="Місяців" value={premMonths}
                onChange={e => setPremMonths(Math.max(1, parseInt(e.target.value) || 1))}
                sx={{ width: 100, ...sx.tf }} />
              <Button size="small" variant="contained"
                onClick={async () => {
                  setPremMsg('');
                  try {
                    const r = await APIJ('/users/' + userDetail.id + '/premium', { months: premMonths, action: 'grant' });
                    if (!r.ok) throw new Error('HTTP ' + r.status);
                    setPremMsg('✅ Видано на ' + premMonths + ' міс.');
                    loadUsers(); loadStats();
                  } catch (e) { setPremMsg('❌ ' + e.message); }
                }}
                sx={{ bgcolor: 'var(--ac1, #7c4dff)', whiteSpace: 'nowrap' }}>
                Видати {premMonths} міс
              </Button>
            </Stack>
            {premMsg && <Alert severity={premMsg.startsWith('✅') ? 'success' : 'error'} sx={{ mb: 1 }} onClose={() => setPremMsg('')}>{premMsg}</Alert>}

            {/* Ліміт читань */}
            <Typography sx={{ color: '#b39ddb', fontSize: 12, mb: 0.8, fontWeight: 600 }}>📖 Бонусні читання</Typography>
            <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              {[0, 1, 3, 5, 10].map(n => (
                <Chip key={n} label={n === 0 ? 'Скинути' : '+' + n} size="small" clickable onClick={() => setLimitsVal(n)}
                  sx={{ bgcolor: limitsVal === n ? 'rgba(57,229,140,.25)' : 'rgba(124,92,255,.15)',
                    color: limitsVal === n ? '#39e58c' : '#cbb8ff', fontWeight: limitsVal === n ? 700 : 400 }} />
              ))}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <TextField size="small" type="number" label="Бонусних" value={limitsVal}
                onChange={e => setLimitsVal(Math.max(0, parseInt(e.target.value) || 0))}
                sx={{ width: 100, ...sx.tf }} />
              <Button size="small" variant="contained"
                onClick={async () => {
                  setLimitsMsg('');
                  try {
                    const r = await APIJ('/users/' + userDetail.id + '/limits', { extra_readings: limitsVal });
                    if (!r.ok) throw new Error('HTTP ' + r.status);
                    setLimitsMsg('✅ Ліміт: +' + limitsVal);
                    loadUsers();
                  } catch (e) { setLimitsMsg('❌ ' + e.message); }
                }}
                sx={{ bgcolor: '#39e58c', color: '#000', whiteSpace: 'nowrap' }}>
                Встановити
              </Button>
            </Stack>
            {limitsMsg && <Alert severity={limitsMsg.startsWith('✅') ? 'success' : 'error'} sx={{ mb: 1 }} onClose={() => setLimitsMsg('')}>{limitsMsg}</Alert>}
          </DialogContent>
          <DialogActions sx={{ bgcolor: '#140f24', justifyContent: 'space-between' }}>
            <Button
              onClick={() => { setTab(3); setUserDetail(null); }}
              sx={{ color: '#39e58c', fontSize: 12 }}>
              ✍️ Написати юзеру
            </Button>
            <Button onClick={() => { setUserDetail(null); setPremMsg(''); setLimitsMsg(''); }} sx={{ color: '#22d3ee' }}>
              Закрити
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog open={!!receiptView} onClose={() => setReceiptView(null)} maxWidth="sm">
        <DialogContent sx={{ bgcolor: '#140f24', p: 1 }}>
          {receiptView && <img src={receiptView} alt="чек" style={{ maxWidth: '100%', maxHeight: '80vh', display: 'block', borderRadius: 8 }} />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!editMp} onClose={() => setEditMp(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#b39ddb' }}>✏️ Редагувати оплату #{editMp?.id}</DialogTitle>
        <DialogContent sx={{ bgcolor: '#140f24' }}>
          <TextField fullWidth type="number" label="Кредити" value={editCredits} onChange={e => setEditCredits(e.target.value)} sx={{ mt: 1, ...sx.tf }} />
          <Typography sx={{ color: '#888', fontSize: 12, mt: 1.5, mb: 0.5 }}>Статус:</Typography>
          <Stack direction="row" spacing={1}>
            {[['approved', '✅ Схвалено'], ['rejected', '❌ Відхилено'], ['pending', '⏳ Очікує']].map(([st, lbl]) => (
              <Button key={st} size="small" variant={editStatus === st ? 'contained' : 'outlined'} onClick={() => setEditStatus(st)} sx={{ fontSize: 11, ...(editStatus === st ? { bgcolor: 'var(--ac1, #7c4dff)' } : { color: '#888', borderColor: '#333' }) }}>{lbl}</Button>
            ))}
          </Stack>
          <Typography sx={{ color: '#666', fontSize: 11, mt: 1.5 }}>Баланс юзера скоригується автоматично на різницю кредитів.</Typography>
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#140f24' }}>
          <Button onClick={() => setEditMp(null)} sx={{ color: '#888' }}>Скасувати</Button>
          <Button variant="contained" onClick={editManual} sx={{ bgcolor: 'var(--ac1, #7c4dff)' }}>Зберегти</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={financeOpen} onClose={() => setFinanceOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#b39ddb' }}>💰 Дохід і прибуток</DialogTitle>
        <DialogContent sx={{ bgcolor: '#140f24' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #2a2a4a' }}>
            <Typography sx={{ color: '#ddd' }}>Дохід (оплати юзерів)</Typography>
            <Typography sx={{ color: '#81c784', fontWeight: 'bold' }}>+€{(finance.income_eur ?? 0).toFixed(2)}</Typography>
          </Box>
          <Typography sx={{ color: '#aaa', fontSize: 12, mt: 1.5, mb: 0.5 }}>⚙️ Авто-затрати (з реальних даних; ціна за одиницю редагована, $):</Typography>
          {(finance.auto || []).map(a => (
            <Box key={a.key} sx={{ display: 'flex', gap: 1, py: 0.6, alignItems: 'center', borderBottom: '1px solid #2a2a4a' }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ color: '#ddd', fontSize: 13 }}>{a.label}</Typography>
                <Typography sx={{ color: '#888', fontSize: 11 }}>{a.qty} шт × $</Typography>
              </Box>
              <TextField size="small" type="number" value={a.key === 'nano' ? unitNano : unitGpt} onChange={e => a.key === 'nano' ? setUnitNano(e.target.value) : setUnitGpt(e.target.value)} onBlur={saveUnits} sx={{ width: 80, ...sx.tf }} />
              <Typography sx={{ color: '#ef9a9a', fontSize: 13, minWidth: 64, textAlign: 'right' }}>−€{(a.eur ?? 0).toFixed(2)}</Typography>
            </Box>
          ))}
          <Typography sx={{ color: '#aaa', fontSize: 12, mt: 1.5, mb: 0.5 }}>✍️ Інші затрати (вручну, курс 1€={finance.usd_per_eur ?? 1.08}$):</Typography>
          {costsLocal.map((c, i) => (
            <Box key={c.id} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
              <TextField size="small" label="Назва" value={c.label} onChange={e => setCostsLocal(cs => cs.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} onBlur={() => saveCost(costsLocal[i])} sx={{ flex: 1, ...sx.tf }} />
              <TextField size="small" type="number" label="Сума" value={c.amount} onChange={e => setCostsLocal(cs => cs.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} onBlur={() => saveCost(costsLocal[i])} sx={{ width: 90, ...sx.tf }} />
              <Button size="small" onClick={() => { const nc = c.currency === 'USD' ? 'EUR' : 'USD'; setCostsLocal(cs => cs.map((x, j) => j === i ? { ...x, currency: nc } : x)); saveCost({ ...c, currency: nc }); }} sx={{ minWidth: 44, color: '#b39ddb' }}>{c.currency}</Button>
              <Button size="small" onClick={() => deleteCost(c.id)} sx={{ color: '#f44336', minWidth: 0 }}>✕</Button>
            </Box>
          ))}
          <Button size="small" onClick={addCost} sx={{ color: 'var(--ac1, #7c4dff)', mt: 0.5 }}>➕ Додати затрату</Button>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, mt: 1, borderTop: '1px solid #2a2a4a' }}>
            <Typography sx={{ color: '#ddd' }}>Затрати разом (авто + ручні)</Typography>
            <Typography sx={{ color: '#ef9a9a', fontWeight: 'bold' }}>−€{(finance.costs_total_eur ?? 0).toFixed(2)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderTop: '2px solid #4a3a7a' }}>
            <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: 18 }}>ЧИСТИЙ ПРИБУТОК</Typography>
            <Typography sx={{ color: (finance.net_eur ?? 0) >= 0 ? '#81c784' : '#ef9a9a', fontWeight: 'bold', fontSize: 18 }}>€{(finance.net_eur ?? 0).toFixed(2)}</Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#140f24' }}>
          <Button onClick={loadFinance} sx={{ color: 'var(--ac1, #7c4dff)' }}>🔄 Оновити</Button>
          <Button onClick={() => setFinanceOpen(false)} sx={{ color: '#888' }}>Закрити</Button>
        </DialogActions>
      </Dialog>
      {/* ── Dialog: Видати преміум (з таблиці кнопка ⭐) ── */}
      <Dialog open={!!premDlg} onClose={() => setPremDlg(null)}
        PaperProps={{ sx: { bgcolor: '#1a1a2e', color: '#fff', border: '1px solid #3a3a5e' } }}>
        <DialogTitle sx={{ color: '#b39ddb' }}>⭐ Видати преміум — {premDlg?.name}</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 1.5 }} flexWrap="wrap">
            {[1, 3, 6, 12].map(m => (
              <Chip key={m} label={m + ' міс'} size="small" clickable onClick={() => setPremMonths(m)}
                sx={{ bgcolor: premMonths === m ? 'rgba(255,213,79,.25)' : 'rgba(124,92,255,.15)',
                  color: premMonths === m ? '#ffd54f' : '#cbb8ff', fontWeight: premMonths === m ? 700 : 400 }} />
            ))}
          </Stack>
          <TextField label="Місяців" type="number" value={premMonths}
            onChange={e => setPremMonths(Math.max(1, parseInt(e.target.value) || 1))}
            sx={{ ...sx.tf }} />
          {premMsg && <Alert severity={premMsg.startsWith('✅') ? 'success' : 'error'} sx={{ mt: 1 }}>{premMsg}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPremDlg(null)} sx={{ color: '#888' }}>Скасувати</Button>
          <Button onClick={grantPrem} variant="contained" sx={{ bgcolor: 'var(--ac1, #7c4dff)' }}>Видати {premMonths} міс</Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Ліміт читань (з таблиці кнопка 📖) ── */}
      <Dialog open={!!limitsDlg} onClose={() => setLimitsDlg(null)}
        PaperProps={{ sx: { bgcolor: '#1a1a2e', color: '#fff', border: '1px solid #3a3a5e' } }}>
        <DialogTitle sx={{ color: '#b39ddb' }}>📖 Бонусні читання — {limitsDlg?.name}</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#888', fontSize: 13, mb: 1.5 }}>
            Додаткові безкоштовні читання понад стандартний ліміт.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap">
            {[0, 1, 3, 5, 10].map(n => (
              <Chip key={n} label={n === 0 ? 'Скинути' : '+' + n} size="small" clickable onClick={() => setLimitsVal(n)}
                sx={{ bgcolor: limitsVal === n ? 'rgba(57,229,140,.25)' : 'rgba(124,92,255,.15)',
                  color: limitsVal === n ? '#39e58c' : '#cbb8ff', fontWeight: limitsVal === n ? 700 : 400 }} />
            ))}
          </Stack>
          <TextField label="Бонусних читань" type="number" value={limitsVal}
            onChange={e => setLimitsVal(Math.max(0, parseInt(e.target.value) || 0))}
            sx={{ ...sx.tf }} />
          {limitsMsg && <Alert severity={limitsMsg.startsWith('✅') ? 'success' : 'error'} sx={{ mt: 1 }}>{limitsMsg}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLimitsDlg(null)} sx={{ color: '#888' }}>Скасувати</Button>
          <Button onClick={setLimits} variant="contained" sx={{ bgcolor: '#39e58c', color: '#000' }}>Встановити</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
